import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Spinner from '../plugins/Spinner';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  ReactFlowProvider,
  reconnectEdge,
  internalsSymbol,
  MarkerType,
  MiniMap,
  Controls,
  ControlButton,
} from 'reactflow';
import 'reactflow/dist/style.css';
import ELK from 'elkjs/lib/elk.bundled.js';
import ElkStepEdge from './BotsContent/Components/ElkStepEdge';
import NodeSearchPalette from './BotsContent/Components/NodeSearchPalette';
import { copyNodeId, shortNodeId } from './BotsContent/Components/NodeEditorChrome';
import { canBeStartNode } from './BotsContent/Components/startNode';
import {
  isStickerUrl,
  buildStickerAction,
  canUseSticker,
} from './BotsContent/Components/stickerMessage';
import useAPI from '../../hooks/useAPI';
import Select from 'react-select';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/theme-github_dark';
import { RichTreeView } from '@mui/x-tree-view';
import { Badge } from '@mui/material';
import NodeModal, {
  NodeModalButton,
} from './BotsContent/Components/NodeModal';
import { describeConditionsSentence } from './BotsContent/Components/nodeSummary';
import {
  buildGraphDiff,
  diffIsEmpty,
  diffRatio,
  graphSnapshot,
} from './BotsContent/Components/graphDiff';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import styledComponent from 'styled-components';
import Countries from '../plugins/Countries.json';
import { Tooltip } from 'react-tooltip';
import { questionToast, sendToast } from '../plugins/toastAll';
import { ToastContainer } from 'react-toastify';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import CustomMenuList from './BotsContent/Components/CustomMenuList';
import { Toaster } from 'react-hot-toast';
import VariableTree from './BotsContent/Components/VariableTree';
import SUTextField from './BotsContent/Components/SUTextField';

import { BsPencilFill, BsTrash3 } from 'react-icons/bs';
import { FaAngleLeft, FaHistory, FaWhatsapp, FaBrain } from 'react-icons/fa';
import { LuListTree } from 'react-icons/lu';
import { MdCleaningServices } from 'react-icons/md';
import { BiTransfer } from 'react-icons/bi';
import { TbHeartRateMonitor, TbLayoutGrid, TbStethoscope } from 'react-icons/tb';
import { IoMdAdd, IoIosSettings } from 'react-icons/io';
import { VscVariableGroup } from 'react-icons/vsc';
import { FaArrowRightArrowLeft } from 'react-icons/fa6';
import { IoClose } from 'react-icons/io5';
import { FiSearch, FiFilter, FiRotateCcw, FiRotateCw, FiHash } from 'react-icons/fi';
import NodePalette from './BotsContent/Others/NodePalette';
import BotCanvasBar, { CanvasShell } from './BotsContent/Others/BotCanvasBar';
import { NodeThemeContext, NODE_THEMES } from './BotsContent/Components/NodeTheme';

import BasicAnswer from './BotsContent/NodeTypes/BasicAnswer';
import TransferAgent from './BotsContent/NodeTypes/TransferAgent';
import Tags from './BotsContent/NodeTypes/Tags';
import AssitantAI from './BotsContent/NodeTypes/AssitantAI';
import SaveAnswer from './BotsContent/NodeTypes/SaveAnswer';
import FlowsWhatsApp from './BotsContent/NodeTypes/FlowsWhatsApp';
import QueryApi from './BotsContent/NodeTypes/QueryApi';
import Conditions from './BotsContent/NodeTypes/Conditions';
import Json from './BotsContent/NodeTypes/Json';
import WebHook from './BotsContent/NodeTypes/WebHook';
import Template from './BotsContent/NodeTypes/Template';

import List from './BotsContent/Others/List';
import Preview from './BotsContent/Others/Preview';
import ErrorMonitor from './BotsContent/Others/ErrorMonitor';
import HistoryVersions from './BotsContent/Others/HistoryVersions';
import DiagnosticsPanel from './BotsContent/Others/DiagnosticsPanel';
import RemoveStatuses from './BotsContent/Others/RemoveStatuses';
import NodeEditorPanel from './BotsContent/Others/NodeEditorPanel';
import AgentNode from './BotsContent/Agent/AgentNode';
import AgentWorkspace from './BotsContent/Agent/AgentWorkspace';
import AgentReadiness from './BotsContent/Agent/AgentReadiness';
import AgentToolPicker from './BotsContent/Agent/AgentToolPicker';
import { createAgentApi } from './BotsContent/Agent/agentApi';
import { AGENT_COLOR, AGENT_NODE_ID, TOOL_TYPES, createDefaultAgentConfig, createTool } from './BotsContent/Agent/agentCatalog';
import { LuActivity, LuHistory, LuRefreshCw, LuSparkles, LuStethoscope } from 'react-icons/lu';

const nodeTypes = {
  basicAnswer: BasicAnswer,
  transferAgent: TransferAgent,
  tags: Tags,
  assistantAI: AssitantAI,
  saveAnswer: SaveAnswer,
  flowsWhatsApp: FlowsWhatsApp,
  queryApi: QueryApi,
  conditions: Conditions,
  json: Json,
  webHook: WebHook,
  template: Template,
  agentAI: AgentNode,
};

const nodeTypesColors = {
  agentAI: AGENT_COLOR,
  basicAnswer: '#00A884',
  transferAgent: '#F56F89',
  tags: '#5BC0DE',
  assistantAI: '#A7ADA7',
  saveAnswer: '#D89C7E',
  flowsWhatsApp: '#B85C7C',
  queryApi: '#7ECCD8',
  conditions: '#E5253D',
  json: '#A7ADA7',
  webHook: '#FC6530',
  template: '#00A884',
};

const elk = new ELK();
const elkLayoutOptions = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.edgeRouting': 'ORTHOGONAL',
  'elk.layered.spacing.nodeNodeBetweenLayers': '90',
  'elk.spacing.nodeNode': '60',
  'elk.spacing.edgeNode': '35',
  'elk.spacing.edgeEdge': '18',
  'elk.layered.spacing.edgeNodeBetweenLayers': '35',
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.layered.nodePlacement.bk.fixedAlignment': 'LEFTUP',
  'elk.layered.nodePlacement.favorStraightEdges': 'true',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  'elk.layered.crossingMinimization.semiInteractive': 'true',
  'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
  'elk.layered.cycleBreaking.strategy': 'GREEDY',
  'elk.layered.thoroughness': '30',
  'elk.layered.mergeEdges': 'false',
  'elk.separateConnectedComponents': 'true',
  'elk.spacing.componentComponent': '90',
};

const ELK_SIDE_BY_POSITION = {
  top: 'NORTH',
  right: 'EAST',
  bottom: 'SOUTH',
  left: 'WEST',
};

const getHandlePorts = (node) => {
  const bounds = node?.[internalsSymbol]?.handleBounds;
  if (!bounds) return null;

  const ports = [];
  for (const type of ['source', 'target']) {
    for (const h of bounds[type] || []) {
      if (!h?.id) continue;
      const side = ELK_SIDE_BY_POSITION[h.position] || (type === 'source' ? 'SOUTH' : 'NORTH');
      ports.push({
        id: h.id,
        width: h.width || 1,
        height: h.height || 1,
        x: h.x,
        y: h.y,
        layoutOptions: { 'elk.port.side': side },
      });
    }
  }
  return ports.length ? ports : null;
};

const getLayoutedNodes = async (nodes, edges) => {
  const portsByNode = {};
  for (const n of nodes) {
    const ports = getHandlePorts(n);
    if (ports) portsByNode[n.id] = new Set(ports.map((p) => p.id));
  }

  const graph = {
    id: 'root',
    layoutOptions: elkLayoutOptions,
    children: nodes.map((n) => {
      const ports = getHandlePorts(n);
      const layoutOptions = {};
      if (n.data?.first) {
        layoutOptions['elk.layered.layering.layerConstraint'] = 'FIRST';
      }
      if (ports) {
        layoutOptions['elk.portConstraints'] = 'FIXED_POS';
      }
      return {
        id: n.id,
        width: n.width || 220,
        height: n.height || 90,
        ...(ports ? { ports } : {}),
        ...(Object.keys(layoutOptions).length ? { layoutOptions } : {}),
      };
    }),
    // Si el handle esta declarado como puerto usamos su id; si no, el del nodo.
    edges: edges.map((e) => ({
      id: e.id,
      sources: [
        portsByNode[e.source]?.has(e.sourceHandle) ? e.sourceHandle : e.source,
      ],
      targets: [
        portsByNode[e.target]?.has(e.targetHandle) ? e.targetHandle : e.target,
      ],
    })),
  };
  const layouted = await elk.layout(graph);

  const layoutedNodes = nodes.map((n) => {
    const elkNode = layouted.children.find((c) => c.id === n.id);
    return elkNode ? { ...n, position: { x: elkNode.x, y: elkNode.y } } : n;
  });

  // ELK ya calcula rutas ortogonales sin solapes entre si; las guardamos para
  // que ElkStepEdge las dibuje en lugar de dejar que cada arista se rutee sola.
  const routes = {};
  for (const elkEdge of layouted.edges || []) {
    const section = elkEdge.sections?.[0];
    if (!section) continue;
    routes[elkEdge.id] = [
      section.startPoint,
      ...(section.bendPoints || []),
      section.endPoint,
    ].map((p) => [Math.round(p.x), Math.round(p.y)]);
  }

  const routedEdges = edges.map((e) => ({
    ...e,
    type: 'elkStep',
    data: { ...(e.data || {}), path: routes[e.id] || null },
  }));

  return { nodes: layoutedNodes, edges: routedEdges };
};

// Estilo base de un ítem del menú "Más opciones": fila con icono + label,
// fondo transparente que se tinta al hover (se aplica por evento).
const dropdownItemStyle = (theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  width: '100%',
  padding: '8px 10px',
  border: 'none',
  borderRadius: '8px',
  backgroundColor: 'transparent',
  color: theme.textPrimary,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
});

const KEEP_ROUTE_DRAG_DISTANCE = 260;

// Cuantos pasos atras se pueden deshacer.
const HISTORY_LIMIT = 30;

// Alcance del modo "aislar rama". Hacia adelante interesa ver que puede pasar
// desde el nodo; hacia atras alcanza con saber quien lo activa.
// La profundidad se ajusta desde el lienzo: sin tope, los ciclos de "volver al
// menu" terminan alcanzando el flujo entero y no se aisla nada.
const FOCUS_DOWNSTREAM_DEPTH = 3;
const FOCUS_UPSTREAM_DEPTH = 1;
const FOCUS_DEPTH_MIN = 1;
const FOCUS_DEPTH_MAX = 20;

const depthButtonStyle = (theme, disabled) => ({
  border: `1px solid ${theme.border}`,
  background: 'transparent',
  color: disabled ? theme.textTertiary : theme.textSecondary,
  borderRadius: '5px',
  width: '20px',
  height: '20px',
  lineHeight: '1',
  padding: 0,
  fontSize: '13px',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.4 : 1,
});

const getId = () => {
  return makeRandomId(10);
};
const makeRandomId = (length) => {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
};

const listCompanyScheduleOptions = [
  {
    label: '24 Horas',
    value: '24h',
  },
  {
    label: 'Horario comercial',
    value: 'companySchedule',
  },
];

const botTypeOptions = [
  {
    label: 'Lineal',
    value: 'linear',
  },
  {
    label: 'Transaccional',
    value: 'transactional',
  },
];

const dateFormatOptions = [
  {
    label: 'Más que fecha actual',
    id: 'current_date',
  },
  {
    label: 'Fecha de hoy',
    id: 'today_date',
  },
  {
    label: 'Fecha de mañana',
    id: 'tomorrow_date',
  },
  {
    label: 'mm/dd/aaaa',
    id: 'mm/dd/aaaa',
  },
  {
    label: 'mm/dd/aa',
    id: 'mm/dd/aa',
  },
  {
    label: 'dd/mm/aaaa',
    id: 'dd/mm/aaaa',
  },
  {
    label: 'dd/mm/aa',
    id: 'dd/mm/aa',
  },
  {
    label: 'dd-mm-aaaa',
    id: 'dd-mm-aaaa',
  },
  {
    label: 'dd-mm-aa',
    id: 'dd-mm-aa',
  },
  {
    label: 'mm-dd-aaaa',
    id: 'mm-dd-aaaa',
  },
  {
    label: 'mm-dd-aa',
    id: 'mm-dd-aa',
  },
  {
    label: 'aaaa-mm-dd',
    id: 'aaaa-mm-dd',
  },
  {
    label: 'm/d/aaaa',
    id: 'm/d/aaaa',
  },
  {
    label: 'm/d/aa',
    id: 'm/d/aa',
  },
  {
    label: 'd/m/aaaa',
    id: 'd/m/aaaa',
  },
  {
    label: 'd/m/aa',
    id: 'd/m/aa',
  },
  {
    label: 'm-d-aaaa',
    id: 'm-d-aaaa',
  },
  {
    label: 'm-d-aa',
    id: 'm-d-aa',
  },
  {
    label: 'd-m-aaaa',
    id: 'd-m-aaaa',
  },
  {
    label: 'd-m-aa',
    id: 'd-m-aa',
  },
  {
    label: 'aaaa-mm-dd',
    id: 'aaaa-mm-dd',
  },
];

const timeFormatOptions = [
  {
    label: 'Más que hora actual',
    id: 'current_time',
  },
  {
    label: 'Más que hora actual + margen',
    id: 'current_time_offset',
  },
  {
    label: 'Menor o igual que hora actual + margen',
    id: 'current_time_max_offset',
  },
  {
    label: 'Menor o igual que hora actual + de mañana + margen',
    id: 'current_time_tomorrow_max_offset',
  },
  {
    label: 'hh:mm',
    id: 'hh:mm',
  },
];

const Flow = () => {
  //# Usos
  const { urlAPI_1, urlAPI_2, urlAPI_3, fetchWithAuth } = useAPI();
  const { data: isDarkTheme } = useQuery({
    queryKey: ['isDarkTheme'],
    queryFn: () => localStorage.getItem('IS_DARK') === 'true',
    staleTime: Infinity,
  });
  const isDark = isDarkTheme ? 'true' : 'false';
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({});

  //# Estados de plugins (Modales, Spinner...)
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState(false);
  const [mode, setMode] = useState('list');
  const [modalCreate, setModalCreate] = useState(false);
  const [variablesModal, setVariablesModal] = useState(false);
  const [variablesOptionsModal, setVariablesOptionsModal] = useState(false);
  const [indexOptionResToNode, setIndexOptionResToNode] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [anchorElConditionsModel, setAnchorElConditionsModel] = useState(null);
  const [responseToNodeModal, setResponseToNodeModal] = useState(false);
  const [typeInput, setTypeInput] = useState('');
  const [typeInputValue, setTypeInputValue] = useState('');
  const [editName, setEditName] = useState('');
  const [showEditName, setShowEditName] = useState(false);
  const [showModalCreateTemplate, setShowModalCreateTemplate] = useState(false);
  const [modalConnectionTest, setModalConnectionTest] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [errorMonitoring, setErrorMonitoring] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedLog, setSelectedLog] = useState(false);
  const [animateClass, setAnimateClass] = useState('');
  const [triggersDuplicateCount, setTriggersDuplicateCount] = useState(0);
  const [transitionsDuplicateCount, setTransitionsDuplicateCount] = useState(0);
  const [showConditionsModal, setShowConditionsModal] = useState(false);
  const [showRemoveStatusesModal, setShowRemoveStatusesModal] = useState(false);
  const [showNodes, setShowNodes] = useState(true);
  const [nodesDrawerWidth, setNodesDrawerWidth] = useState(300);
  const [modalBotAssistant, setModalBotAssistant] = useState(false);
  const [watchSelectedNode, setWatchSelectedNode] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const animationFrameId = useRef(null);
  const [nodeContextModal, setNodeContextModal] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuNode, setContextMenuNode] = useState(null);
  const [isNodeExpanded, setIsNodeExpanded] = useState(false);

  //# Estados del Modulo
  const [listBotsTemplates, setListBotsTemplates] = useState([]);
  const [listBots, setListBots] = useState([]);
  const [listLogs, setListLogs] = useState([]);
  const [nameBot, setNameBot] = useState('');
  const [descriptionBot, setDescriptionBot] = useState('');
  const [botType, setBotType] = useState('linear');
  const [listAreas, setListAreas] = useState([]);
  const [listAdvisers, setListAdvisers] = useState([]);
  const [listTags, setListTags] = useState([]);
  const [listStages, setListStages] = useState([]);
  const [listLists, setListLists] = useState([]);
  const [listAssistants, setListAssistants] = useState([]);
  const [correspondence, setCorrespondence] = useState([]);
  const [listOptionsResToNode, setListOptionsResToNode] = useState([]);
  const [flows, setFlows] = useState([]);
  const [listInactivity, setListInactivity] = useState([]);
  const [listAttempts, setListAttempts] = useState([]);
  const [listDisconnectedNodes, setListDisconnectedNodes] = useState([]);
  const [listDisconnectedOptions, setListDisconnectedOptions] = useState([]);
  const [listNodesWithErrorInFields, setListNodesWithErrorInFields] = useState(
    []
  );
  const botDetailsCache = useRef({});
  const lastSavedGraph = useRef(null);
  const botRev = useRef(null);
  const [listOptionsOperators, setListOptionsOperators] = useState([]);

  // Estado de salud para el badge del engranaje "Más opciones": rojo si hay
  // errores reales, ámbar si solo advertencias, nada si el flujo está sano.
  // Hace que el acceso al Monitor pida atención justo cuando importa.
  const monitorHasError =
    listNodesWithErrorInFields.length > 0 ||
    triggersDuplicateCount > 1 ||
    transitionsDuplicateCount > 1;
  const monitorHasWarning =
    listDisconnectedNodes.length > 0 || listDisconnectedOptions.length > 0;
  const monitorDotColor = monitorHasError
    ? '#F87171'
    : monitorHasWarning
    ? '#F59E0B'
    : null;

  // FLUJO
  const { screenToFlowPosition, fitBounds, fitView, setCenter } = useReactFlow();
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const memoizedNodeTypes = useMemo(() => nodeTypes, []);
  const memoizedEdgeTypes = useMemo(() => ({ elkStep: ElkStepEdge }), []);
  const reconnectDone = useRef(true);

  const historyRef = useRef({ past: [], future: [] });
  const [historyState, setHistoryState] = useState({ canUndo: false, canRedo: false });
  const skipHistoryRef = useRef(false);

  const syncHistoryFlags = () => {
    setHistoryState({
      canUndo: historyRef.current.past.length > 0,
      canRedo: historyRef.current.future.length > 0,
    });
  };

  const commitHistory = useCallback((currentNodes, currentEdges) => {
    if (skipHistoryRef.current) return;
    const snapshot = {
      nodes: currentNodes.map((n) => ({ ...n, data: { ...n.data } })),
      edges: currentEdges.map((e) => ({ ...e, data: { ...(e.data || {}) } })),
    };
    const { past } = historyRef.current;
    past.push(snapshot);
    if (past.length > HISTORY_LIMIT) past.shift();
    historyRef.current.future = [];
    syncHistoryFlags();
  }, []);

  const applySnapshot = (snapshot) => {
    skipHistoryRef.current = true;
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    setSelectedNode(null);
    setTimeout(() => {
      skipHistoryRef.current = false;
    }, 0);
  };

  const undo = useCallback(() => {
    const { past, future } = historyRef.current;
    if (!past.length) return;
    const previous = past.pop();
    future.push({
      nodes: nodes.map((n) => ({ ...n, data: { ...n.data } })),
      edges: edges.map((e) => ({ ...e, data: { ...(e.data || {}) } })),
    });
    applySnapshot(previous);
    syncHistoryFlags();
    sendToast({ type: 'info', message: 'Cambio deshecho.' });
  }, [nodes, edges]);

  const redo = useCallback(() => {
    const { past, future } = historyRef.current;
    if (!future.length) return;
    const next = future.pop();
    past.push({
      nodes: nodes.map((n) => ({ ...n, data: { ...n.data } })),
      edges: edges.map((e) => ({ ...e, data: { ...(e.data || {}) } })),
    });
    applySnapshot(next);
    syncHistoryFlags();
  }, [nodes, edges]);

  const [searchOpen, setSearchOpen] = useState(false);
  const [flashNodeId, setFlashNodeId] = useState(null);
  const flashTimerRef = useRef(null);

  const goToSearchResult = (node) => {
    setSearchOpen(false);
    if (!node?.position) return;
    goToNode(node);
    setFlashNodeId(node.id);
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setFlashNodeId(null), 1800);
  };

  const dragOriginRef = useRef(null);
  const branchDragRef = useRef(null);

  const onNodeDragStart = (_, node) => {
    if (node?.id === AGENT_NODE_ID) return;
    dragOriginRef.current = node?.position ? { ...node.position } : null;
    commitHistory(nodes, edges);

    if (isolatedNodeId && connectedNodeIds?.has(node?.id)) {
      branchDragRef.current = {
        origin: { ...node.position },
        startPositions: new Map(
          nodes
            .filter((n) => connectedNodeIds.has(n.id) && n.id !== node.id)
            .map((n) => [n.id, { ...n.position }])
        ),
      };
    } else {
      branchDragRef.current = null;
    }
  };

  const onNodeDrag = (_, node) => {
    const ctx = branchDragRef.current;
    if (!ctx || !node?.position) return;

    const dx = node.position.x - ctx.origin.x;
    const dy = node.position.y - ctx.origin.y;

    setNodes((nds) =>
      nds.map((n) => {
        const start = ctx.startPositions.get(n.id);
        if (!start) return n;
        return { ...n, position: { x: start.x + dx, y: start.y + dy } };
      })
    );
  };

  const onNodeDragStop = (_, node) => {
    const origin = dragOriginRef.current;
    const branchCtx = branchDragRef.current;
    dragOriginRef.current = null;
    branchDragRef.current = null;
    if (!node?.id || !origin) return;

    const dx = node.position.x - origin.x;
    const dy = node.position.y - origin.y;
    const moved = Math.hypot(dx, dy);

    // Rama completa: los nodos conservan su posicion relativa, asi que las rutas
    // internas siguen sirviendo trasladadas. Solo las que cruzan el borde de la
    // rama pierden sentido.
    if (branchCtx) {
      const movedIds = new Set([node.id, ...branchCtx.startPositions.keys()]);
      setEdges((eds) =>
        eds.map((e) => {
          const sourceMoved = movedIds.has(e.source);
          const targetMoved = movedIds.has(e.target);
          if (!sourceMoved && !targetMoved) return e;

          if (sourceMoved && targetMoved) {
            const path = e.data?.path;
            if (!Array.isArray(path)) return e;
            return {
              ...e,
              data: {
                ...e.data,
                path: path.map(([x, y]) => [Math.round(x + dx), Math.round(y + dy)]),
              },
            };
          }
          return { ...e, data: { ...(e.data || {}), path: null } };
        })
      );
      return;
    }

    if (moved <= KEEP_ROUTE_DRAG_DISTANCE) return;

    setEdges((eds) =>
      eds.map((e) =>
        e.source === node.id || e.target === node.id
          ? { ...e, data: { ...(e.data || {}), path: null } }
          : e
      )
    );
  };
  const [selectedNode, setSelectedNode] = useState(null);
  const agentApi = useMemo(() => createAgentApi(fetchWithAuth, urlAPI_3), []);
  const [agentDraft, setAgentDraft] = useState(null);
  const [agentSaved, setAgentSaved] = useState(null);
  const [agentSelection, setAgentSelection] = useState(null);
  const [agentSaving, setAgentSaving] = useState(false);
  const [agentValidation, setAgentValidation] = useState(null);
  const [agentView, setAgentView] = useState('agent');
  const [agentTesting, setAgentTesting] = useState(false);
  const [agentTested, setAgentTested] = useState(false);
  const [agentLiveTools, setAgentLiveTools] = useState([]);
  const agentLiveTimer = useRef(null);
  const handleAgentTurn = useCallback((used) => {
    setAgentTested(true);
    if (!used?.length) return;
    setAgentLiveTools(used);
    clearTimeout(agentLiveTimer.current);
    agentLiveTimer.current = setTimeout(() => setAgentLiveTools([]), 4000);
  }, []);
  const agentMode = Boolean(agentDraft?.enabled);
  const withoutPosition = (config) => (config ? JSON.stringify({ ...config, position: undefined }) : 'null');
  const agentDirty = Boolean(agentDraft) && withoutPosition(agentDraft) !== withoutPosition(agentSaved);
  const agentBotIdRef = useRef(null);
  const pendingAgentRef = useRef(false);
  const agentOpenOnLoadRef = useRef(false);

  const cleanAgentConfig = (config) => ({
    ...config,
    tools: (config.tools || []).map((tool) =>
      tool.config?.sampleText !== undefined ? { ...tool, config: { ...tool.config, sampleText: undefined } } : tool
    ),
  });

  const saveAgentConfig = async (config, { silent = false } = {}) => {
    const botId = agentBotIdRef.current;
    if (!botId || !config) return false;
    const payload = JSON.parse(JSON.stringify(cleanAgentConfig(config)));
    if (!silent) setAgentSaving(true);
    try {
      const result = await agentApi.save(botId, payload);
      const stored = result.agentConfig ? JSON.parse(JSON.stringify(result.agentConfig)) : payload;
      setAgentValidation(result.validation || null);
      setAgentSaved(stored);
      if (stored !== payload) {
        setAgentDraft((draft) => (draft ? { ...stored, position: draft.position || stored.position } : draft));
      }
      setEditFlow((prev) => (prev && prev._id === botId ? { ...prev, agentConfig: stored } : prev));
      if (botDetailsCache.current[botId]) {
        botDetailsCache.current[botId] = { ...botDetailsCache.current[botId], agentConfig: stored };
      }
      if (!silent) sendToast({ type: 'success', message: 'Agente guardado.' });
      return true;
    } catch (error) {
      setAgentValidation(error.validation || null);
      if (!silent) sendToast({ type: 'error', message: error.message || 'No se pudo guardar el agente.' });
      return false;
    } finally {
      if (!silent) setAgentSaving(false);
    }
  };

  const openAgent = (tab = 'instructions') => {
    setSelectedNode(null);
    if (!agentDraft) setAgentDraft({ ...createDefaultAgentConfig(), enabled: false });
    setAgentSelection({ view: 'agent', tab });
    setAgentView('agent');
    setAgentTesting(false);
  };

  const [showToolPicker, setShowToolPicker] = useState(false);
  const [toolPickerType, setToolPickerType] = useState(null);

  const addAgentTool = useCallback((type) => {
    if (!TOOL_TYPES[type]) return;
    setAgentDraft((draft) => {
      if (!draft) return draft;
      const current = draft.tools || [];
      const existing = TOOL_TYPES[type].single ? current.findIndex((tool) => tool.type === type) : -1;
      if (existing >= 0) {
        setAgentSelection({ view: 'tool', toolIndex: existing });
        return draft;
      }
      const tools = [...current, createTool(type, current)];
      setAgentSelection({ view: 'tool', toolIndex: tools.length - 1 });
      return { ...draft, tools };
    });
    setShowToolPicker(false);
    setSelectedNode(null);
    setAgentView('agent');
    setAgentTesting(false);
  }, []);

  const selectAgentTool = useCallback((toolIndex) => {
    setSelectedNode(null);
    setAgentSelection({ view: 'tool', toolIndex });
    setAgentView('agent');
    setAgentTesting(false);
  }, []);

  const openToolPicker = useCallback((type) => {
    setSelectedNode(null);
    setToolPickerType(typeof type === 'string' ? type : null);
    setShowToolPicker(true);
  }, []);

  const [hoveredNodeId, setHoveredNodeId] = useState(null);
  const hoverClearTimer = useRef(null);

  const nodeTheme = isDark === 'true' ? NODE_THEMES.dark : NODE_THEMES.light;
  const conditionsSentence = describeConditionsSentence(
    selectedNode?.data?.conditions
  );

  // Al entrar a un nodo: cancelamos cualquier limpieza pendiente y fijamos el foco.
  // Al salir: NO limpiamos de inmediato, esperamos un instante. Si el cursor entra
  // a otro nodo en esa ventana, se cancela la limpieza — así se evita el destello
  // de "todo iluminado" al pasar el cursor entre nodos (parpadeo del modo foco).
  const handleNodeMouseEnter = (_, node) => {
    if (node?.id === AGENT_NODE_ID) return;
    if (hoverClearTimer.current) {
      clearTimeout(hoverClearTimer.current);
      hoverClearTimer.current = null;
    }
    setHoveredNodeId(node.id);
  };
  const handleNodeMouseLeave = () => {
    if (hoverClearTimer.current) clearTimeout(hoverClearTimer.current);
    hoverClearTimer.current = setTimeout(() => {
      setHoveredNodeId(null);
      hoverClearTimer.current = null;
    }, 120);
  };

  // Modo "aislar rama": se activa con el boton del canvas y ancla el foco a un
  // nodo. Mientras esta activo el hover no manda, para que la rama no cambie
  // sola al mover el cursor.
  const [isolatedNodeId, setIsolatedNodeId] = useState(null);
  const [focusDepth, setFocusDepth] = useState(FOCUS_DOWNSTREAM_DEPTH);

  const focusNodeId = isolatedNodeId || hoveredNodeId || selectedNode?.id || null;

  const connectedNodeIds = useMemo(() => {
    if (!focusNodeId) return null;

    // Vecinos directos cuando el foco es pasajero; rama completa (todo lo que
    // lleva al nodo y todo lo que sale de el) cuando esta aislado.
    if (!isolatedNodeId) {
      const ids = new Set([focusNodeId]);
      edges.forEach((e) => {
        if (e.source === focusNodeId) ids.add(e.target);
        if (e.target === focusNodeId) ids.add(e.source);
      });
      return ids;
    }

    const outgoing = new Map();
    const incoming = new Map();
    for (const e of edges) {
      if (!outgoing.has(e.source)) outgoing.set(e.source, []);
      if (!incoming.has(e.target)) incoming.set(e.target, []);
      outgoing.get(e.source).push(e.target);
      incoming.get(e.target).push(e.source);
    }

    const ids = new Set([focusNodeId]);
    const expand = (graph, maxDepth) => {
      let frontier = [focusNodeId];
      const visited = new Set([focusNodeId]);
      for (let depth = 0; depth < maxDepth && frontier.length; depth++) {
        const nextFrontier = [];
        for (const current of frontier) {
          for (const next of graph.get(current) || []) {
            if (visited.has(next)) continue;
            visited.add(next);
            ids.add(next);
            nextFrontier.push(next);
          }
        }
        frontier = nextFrontier;
      }
    };

    expand(outgoing, focusDepth);
    expand(incoming, FOCUS_UPSTREAM_DEPTH);
    return ids;
  }, [focusNodeId, isolatedNodeId, focusDepth, edges]);

  const focusHasMore = useMemo(() => {
    if (!isolatedNodeId || !connectedNodeIds) return false;
    return edges.some(
      (e) => connectedNodeIds.has(e.source) && !connectedNodeIds.has(e.target)
    );
  }, [isolatedNodeId, connectedNodeIds, edges]);

  const errorNodeIdsKey = useMemo(
    () =>
      [...listDisconnectedNodes, ...listDisconnectedOptions, ...listNodesWithErrorInFields]
        .map((n) => n.id)
        .sort()
        .join('|'),
    [listDisconnectedNodes, listDisconnectedOptions, listNodesWithErrorInFields]
  );
  const errorNodeIds = useMemo(
    () => new Set(errorNodeIdsKey ? errorNodeIdsKey.split('|') : []),
    [errorNodeIdsKey]
  );

  const agentOrphanNodes = useMemo(() => {
    if (!agentMode) return [];
    const reachable = new Set(nodes.filter((n) => n.type === 'webHook').map((n) => n.id));
    const queue = [...reachable];
    while (queue.length) {
      const current = queue.shift();
      for (const edge of edges) {
        if (edge.source === current && !reachable.has(edge.target)) {
          reachable.add(edge.target);
          queue.push(edge.target);
        }
      }
    }
    return nodes.filter((n) => !reachable.has(n.id)).map((n) => ({ id: n.id, title: n.data?.title || n.type, type: n.type }));
  }, [agentMode, nodes, edges]);
  const agentOrphanIds = useMemo(() => new Set(agentOrphanNodes.map((n) => n.id)), [agentOrphanNodes]);

  const agentReadinessItems = (() => {
    if (!agentDraft) return [];
    const ins = agentDraft.instructions || {};
    const tools = agentDraft.tools || [];
    const missing = tools.findIndex((tool) => !String(tool.whenToUse || '').trim());
    const suggested = tools.filter((tool) => tool.suggested).length;
    const hasInstructions = Boolean(String(ins.role || '').trim() && String(ins.personality || '').trim());
    const toolsReady = tools.length > 0 && missing < 0;
    const saved = !agentDirty && Boolean(agentSaved);
    let toolsDetail = `${tools.length} configurada${tools.length === 1 ? '' : 's'}`;
    if (!tools.length) toolsDetail = 'Agrega al menos una herramienta';
    else if (missing >= 0) toolsDetail = `«${tools[missing].label || tools[missing].name}» no dice cuándo usarla`;
    else if (suggested) toolsDetail += ` · ${suggested} sugerencia${suggested === 1 ? '' : 's'} por revisar`;
    let toolsAction = null;
    if (!tools.length) toolsAction = () => openToolPicker();
    else if (missing >= 0) toolsAction = () => selectAgentTool(missing);
    else if (suggested) toolsAction = () => selectAgentTool(tools.findIndex((tool) => tool.suggested));
    return [
      {
        key: 'instructions',
        label: 'Instrucciones',
        done: hasInstructions,
        detail: hasInstructions ? 'Rol y personalidad definidos' : 'Falta el rol o la personalidad',
        onClick: () => openAgent('instructions'),
      },
      { key: 'tools', label: 'Herramientas', done: toolsReady, detail: toolsDetail, onClick: toolsAction },
      {
        key: 'tested',
        label: 'Probado en el chat',
        done: agentTested,
        detail: agentTested ? 'Ya conversaste con el agente' : 'Conversa con el agente antes de publicar',
        onClick: () => {
          setAgentView('agent');
          setAgentTesting(true);
        },
      },
      {
        key: 'saved',
        label: 'Guardado',
        done: saved,
        detail: saved ? 'Sin cambios pendientes' : 'Hay cambios sin guardar',
        onClick: saved ? null : () => saveAgentConfig(agentDraft),
      },
    ];
  })();

  const displayNodes = useMemo(() => {
    const flowNodes = nodes.map((n) => {
      const hasError = errorNodeIds.has(n.id) || agentOrphanIds.has(n.id);
      return {
      ...n,
      deletable: !n.data?.first,
      data:
        Boolean(n.data?.error) === hasError
          ? n.data
          : { ...n.data, error: hasError },
      style: {
        ...n.style,
        opacity: !connectedNodeIds || connectedNodeIds.has(n.id) ? 1 : 0.35,
        transition: 'opacity 0.2s ease',
        ...(n.id === flashNodeId
          ? { outline: `2px solid ${nodeTheme.accent}`, outlineOffset: '4px', borderRadius: '10px' }
          : null),
      },
      };
    });
    return flowNodes;
  }, [nodes, connectedNodeIds, flashNodeId, nodeTheme, errorNodeIds, agentOrphanIds]);

  const displayEdges = useMemo(() => {
    return edges.map((e) => {
      const isConnected = isolatedNodeId
        ? connectedNodeIds?.has(e.source) && connectedNodeIds?.has(e.target)
        : focusNodeId && (e.source === focusNodeId || e.target === focusNodeId);
      const opacity = focusNodeId ? (isConnected ? 1 : 0.3) : 1;
      return {
        ...e,
        type: 'elkStep',
        style: {
          ...e.style,
          stroke: isConnected ? '#5BC0DE' : nodeTheme.edge,
          strokeWidth: isConnected ? 2.5 : 2,
          opacity,
          transition: 'stroke 0.2s ease, stroke-width 0.2s ease, opacity 0.2s ease',
        },
        markerEnd: e.markerEnd ? { ...e.markerEnd, color: isConnected ? '#5BC0DE' : nodeTheme.edge } : e.markerEnd,
        zIndex: 0,
      };
    });
  }, [edges, focusNodeId, isolatedNodeId, connectedNodeIds, nodeTheme]);

  useEffect(() => {
    const isTyping = (target) => {
      if (!target) return false;
      const tag = target.tagName;
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        target.isContentEditable
      );
    };

    const onKeyDown = (event) => {
      const mod = event.ctrlKey || event.metaKey;

      if (mod && event.key.toLowerCase() === 'f') {
        if (isTyping(event.target)) return;
        event.preventDefault();
        setSearchOpen(true);
        return;
      }

      if (searchOpen) return;
      if (isTyping(event.target)) return;

      if (mod && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }

      if (mod && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        redo();
        return;
      }

      if (event.key === 'Escape') {
        setIsolatedNodeId(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [searchOpen, undo, redo]);

  const handleFlowInit = useCallback((instance) => {
    const flowNodes = instance.getNodes();
    const startNode = flowNodes.find((n) => n.id === AGENT_NODE_ID) || flowNodes.find((n) => n.data?.first) || flowNodes[0];
    if (!startNode?.position) return;
    const w = startNode.width || 220;
    const h = startNode.height || 90;
    instance.setCenter(startNode.position.x + w / 2, startNode.position.y + h / 2, { zoom: 0.85 });
  }, []);

  const [autoLayoutRunning, setAutoLayoutRunning] = useState(false);
  const handleAutoLayout = async () => {
    if (autoLayoutRunning || nodes.length === 0) return;
    setAutoLayoutRunning(true);
    commitHistory(nodes, edges);
    try {
      const { nodes: layoutedNodes, edges: routedEdges } = await getLayoutedNodes(
        nodes,
        edges
      );
      setNodes(layoutedNodes);
      setEdges(routedEdges);
      await updateBot(editFlow, routedEdges, layoutedNodes);
      const firstNode =
        layoutedNodes.find((n) => n.data?.first) || layoutedNodes[0];
      setTimeout(() => {
        if (firstNode?.position) {
          const w = firstNode.width || 220;
          const h = firstNode.height || 90;
          setCenter(
            firstNode.position.x + w / 2,
            firstNode.position.y + h / 2,
            { zoom: 0.85, duration: 600 }
          );
        } else {
          fitView({ padding: 0.2, duration: 400 });
        }
      }, 60);
    } finally {
      setAutoLayoutRunning(false);
    }
  };

  const [requestResponse, setRequestResponse] = useState({});
  const [editFlow, setEditFlow] = useState({});

  useEffect(() => {
    const botId = editFlow?._id || null;
    if (agentBotIdRef.current === botId) return;
    agentBotIdRef.current = botId;
    const saved = editFlow?.agentConfig ? JSON.parse(JSON.stringify(editFlow.agentConfig)) : null;
    setAgentSaved(saved);
    setAgentDraft(saved);
    setAgentSelection(agentOpenOnLoadRef.current && saved ? { view: 'agent', tab: 'instructions' } : null);
    agentOpenOnLoadRef.current = false;
    setAgentValidation(null);
    setAgentTested(false);
    setAgentLiveTools([]);
    setAgentView('agent');
    setAgentTesting(false);
  }, [editFlow?._id]);

  const showAgentWorkspace = agentMode && agentView === 'agent' && !selectedLog;
  useEffect(() => {
    if (!agentMode || agentView !== 'events') return undefined;
    const timer = setTimeout(() => fitView({ padding: 0.2, duration: 300 }), 80);
    return () => clearTimeout(timer);
  }, [agentMode, agentView, fitView]);

  const [connectionTestData, setConnectionTestData] = useState({
    headers: [],
    params: [],
    data: [],
  });
  const [connectionTestDataParams, setConnectionTestDataParams] = useState({
    url: null,
    protocol: null,
    auth: null,
  });
  const [listToNodeData, setListToNodeData] = useState({
    options: [],
    description: '',
  });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [percentBar, setPercentBar] = useState(0);
  const [loadingPercentBar, setLoadingPercentBar] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [valueSelectedVariable, setValueSelectedVariable] = useState(null);
  const [contextVariables, setContextVariables] = useState([]);
  const [contextResponseApi, setContextResponseApi] = useState([]);

  // #--> FLUJO
  const onConnect = (params) => {
    let color = '';
    if (params.source.includes('basicAnswer')) {
      color = nodeTypesColors['basicAnswer'];
    }
    if (params.source.includes('transferAgent')) {
      color = nodeTypesColors['transferAgent'];
    }
    if (params.source.includes('tags')) {
      color = nodeTypesColors['tags'];
    }
    if (params.source.includes('assistantAI')) {
      color = nodeTypesColors['assistantAI'];
    }
    if (params.source.includes('saveAnswer')) {
      color = nodeTypesColors['saveAnswer'];
    }
    if (params.source.includes('flowsWhatsApp')) {
      color = nodeTypesColors['flowsWhatsApp'];
    }
    if (params.source.includes('queryApi')) {
      color = nodeTypesColors['queryApi'];
    }
    if (params.source.includes('conditions')) {
      color = nodeTypesColors['conditions'];
    }
    if (params.source.includes('json')) {
      color = nodeTypesColors['json'];
    }
    if (params.source.includes('webHook')) {
      color = nodeTypesColors['webHook'];
    }
    if (params.source.includes('template')) {
      color = nodeTypesColors['template'];
    }

    let edgeRetunr = false;
    if (params.targetHandle.includes('target-return')) {
      edgeRetunr = true;
    }

    let newNodes = nodes;
    if (!edgeRetunr) {
      const result = setDataFromChildToParent(params);
      newNodes = result;
    }

    const newEdge = {
      ...params,
      type: 'elkStep',
      animated: edgeRetunr,
      label: validateTransitionsLength(params.source),
      labelStyle: { fontSize: 8, fontWeight: 700, fill: '#ffffff' },
      labelBgStyle: { fill: color },
      labelBgPadding: [4, 2],
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 15,
        height: 15,
        color: color,
      },
      style: {
        strokeWidth: 2,
        stroke: color,
      },
      zIndex: 0,
    };

    commitHistory(nodes, edges);
    const updatedEdges = addEdge(newEdge, edges);
    setEdges(updatedEdges);

    setTimeout(async () => {
      await updateBot(editFlow, updatedEdges, newNodes);
    }, 1000);
  };

  const onDrop = (event) => {
    event.preventDefault();

    const type = event.dataTransfer.getData('application/reactflow');

    if (typeof type === 'undefined' || !type) {
      return;
    }

    commitHistory(nodes, edges);

    let newNode;
    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });
    if (type == 'BasicAnswer') {
      newNode = {
        id: getId() + '-basicAnswer',
        type: 'basicAnswer',
        data: {
          title: 'Respues Básica',
          bot_response: '',
          user_response: null,
          options: [],
          file: null,
          file_url_variable: null,
          file_type_variable: null,
          variables: null,
          saved: false,
          advanced: {
            request_name: null,
            confirm_name: null,
            edit_name: null,
            dynamic_buttons: false,
            dynamic_buttons_variable: null,
            dynamic_buttons_variable_description: null,
            closeChatBot: null,
            nextFlow: null,
            regularExpressions: {
              active: false,
              type: null,
              format: null,
              errorMessage: null,
            },
          },
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'TransferAgent') {
      newNode = {
        id: getId() + '-transferAgent',
        type: 'transferAgent',
        data: {
          title: 'Transferir Agente/Area',
          first: false,
          bot_response: '',
          user_response: null,
          agent_area: null,
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'Tags') {
      newNode = {
        id: getId() + '-tags',
        type: 'tags',
        data: {
          title: 'Etiquetar',
          first: false,
          user_response: null,
          tags: null,
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'AssitantAI') {
      newNode = {
        id: getId() + '-assistantAI',
        type: 'assistantAI',
        data: {
          title: 'Asistente IA',
          user_response: null,
          assistant: null,
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'SaveAnswer') {
      newNode = {
        id: getId() + '-saveAnswer',
        type: 'saveAnswer',
        data: {
          title: 'Guardar Respuesta',
          user_response: '*',
          variable: null,
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'FlowsWhatsApp') {
      newNode = {
        id: getId() + '-flowsWhatsApp',
        type: 'flowsWhatsApp',
        data: {
          title: 'Flujo de WhatsApp',
          user_response: null,
          flow: null,
          flow_data: null,
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'QueryApi') {
      newNode = {
        id: getId() + '-queryApi',
        type: 'queryApi',
        data: {
          title: 'Consulta API',
          user_response: null,
          flow_template_id: null,
          flow_template_name: null,
          succes: 'Exito',
          failed: 'Fallo',
          url_api: null,
          auth: null,
          protocol: null,
          user: null,
          password: null,
          headers: [],
          requestParams: [],
          requestParamsBody: [],
          responseParams: [],
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'Conditions') {
      newNode = {
        id: getId() + '-conditions',
        type: 'conditions',
        data: {
          title: 'Condiciones',
          control_message: null,
          succes: 'Cumple condición',
          failed: 'Falla condición',
          conditions: [],
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'Json') {
      newNode = {
        id: getId() + '-json',
        type: 'json',
        data: {
          title: 'JSON',
          user_response: null,
          json: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'WebHook') {
      newNode = {
        id: getId() + '-webHook',
        type: 'webHook',
        data: {
          title: 'WebHook',
          user_response: '*',
          url: `https://85xq25jayj.execute-api.us-east-1.amazonaws.com/dev/chatBotWebhooks/${editFlow._id}`,
          body: null,
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }
    if (type == 'Template') {
      newNode = {
        id: getId() + '-template',
        type: 'template',
        data: {
          title: 'Enviar Plantilla',
          user_response: null,
          template_id: null,
          template_name: null,
          parametersBody: [],
          parametersMedia: {},
          parameterUrl: null,
          variables: null,
          saved: false,
          onRemove: (value) => removeNode(value),
        },
        position,
      };
    }

    createLogs({
      nodes: nodes,
      edges: edges,
      flows: editFlow.flows,
      id: editFlow._id,
    });
    setNodes((nds) => [...nds, newNode]);
    let newNodes = [...nodes, newNode];
    setTimeout(() => {
      addNewNode(editFlow, newNodes, newNode);
    }, 500);
  };

  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  // Mientras se arrastra una conexión (nueva o reconexión) el canvas lleva la
  // clase `is-connecting`: las entradas se muestran como zonas para soltar.
  const [isConnecting, setIsConnecting] = useState(false);
  const onConnectStart = () => setIsConnecting(true);
  const onConnectEnd = () => setIsConnecting(false);

  const onReconnectStart = () => {
    reconnectDone.current = false;
    setIsConnecting(true);
  };

  const onReconnect = (oldEdge, newConnection) => {
    reconnectDone.current = true;
    let reconnect = reconnectEdge(oldEdge, newConnection, edges);
    setEdges(reconnect);

    setTimeout(async () => {
      await updateBot(editFlow, reconnect, nodes);
    }, 500);
  };

  const onReconnectEnd = (_, edge) => {
    setIsConnecting(false);
    if (!reconnectDone.current) {
      let newEdges = edges.filter((e) => e.id !== edge.id);
      setEdges(newEdges);
      const newNodes = removeDataFromChildToParent(edge);

      setTimeout(async () => {
        await updateBot(editFlow, newEdges, newNodes);
      }, 500);
    }

    reconnectDone.current = true;
  };

  const handleNodeClick = (event, node) => {
    // Solo procesar click izquierdo (button 0)
    if (event.button !== 0) return;
    if (node.id === AGENT_NODE_ID) {
      setSelectedNode(null);
      setAgentSelection((current) => (current?.view ? current : { view: 'agent', tab: 'instructions' }));
      return;
    }
    setAgentSelection(null);
    if (!node.parentId) {
      const modalWidth = 300;
      const modalHeight = 460;
      let x = event.clientX;
      let y = event.clientY;
      
      if (x + modalWidth > window.innerWidth) {
        x = window.innerWidth - modalWidth - 20;
      }
      if (y + modalHeight > window.innerHeight) {
        y = window.innerHeight - modalHeight - 20;
      }
      if (x < 0) x = 20;
      if (y < 0) y = 20;
      
      setPosition({ x, y });
      setSelectedNode(node);
      setWatchSelectedNode(node.id);
    }
  };

  const handleNodeContextMenu = (event, node) => {
    event.preventDefault();
    event.stopPropagation();
    if (node?.id === AGENT_NODE_ID) return;
    if (!node.parentId) {
      const modalWidth = 200;
      const modalHeight = 100;
      let x = event.clientX;
      let y = event.clientY;
      
      if (x + modalWidth > window.innerWidth) {
        x = window.innerWidth - modalWidth - 20;
      }
      if (y + modalHeight > window.innerHeight) {
        y = window.innerHeight - modalHeight - 20;
      }
      if (x < 0) x = 20;
      if (y < 0) y = 20;
      
      setContextMenuPosition({ x, y });
      setContextMenuNode(node);
      setNodeContextModal(true);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseMove = useRef((e) => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }
    
    animationFrameId.current = requestAnimationFrame(() => {
      const modalWidth = 300;
      const modalHeight = 460;
      let x = e.clientX - dragOffset.current.x;
      let y = e.clientY - dragOffset.current.y;
      
      // Mantener el modal dentro de la pantalla
      if (x + modalWidth > window.innerWidth) {
        x = window.innerWidth - modalWidth;
      }
      if (y + modalHeight > window.innerHeight) {
        y = window.innerHeight - modalHeight;
      }
      if (x < 0) x = 0;
      if (y < 0) y = 0;
      
      setPosition({ x, y });
    });
  }).current;

  const handleMouseUp = useRef(() => {
    setIsDragging(false);
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }
  }).current;

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true });
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null;
        }
      };
    }
  }, [isDragging]);

  const optionLabelFromHandle = (sourceHandle) => {
    const handleId = String(sourceHandle).replace(/^source-/, '');

    for (const node of nodes) {
      const option = (node.data?.options || []).find(
        (opt) => opt.id === handleId || handleId.startsWith(`${opt.id}-`)
      );
      if (option) {
        return option.label;
      }
    }

    return null;
  };

  const setDataFromChildToParent = (params) => {
    if (!params || !params.sourceHandle || !params.target) {
      console.error('Invalid parameters', params);
      return nodes;
    }

    const sHandle = params.sourceHandle.replace(/^source-/, '');

    let optionLabel = null;
    if (sHandle.includes('conditions')) {
      optionLabel = sHandle.includes('success')
        ? 'Cumple condición'
        : 'Falla condición';
    } else {
      optionLabel = optionLabelFromHandle(params.sourceHandle);
    }

    if (optionLabel) {
      const newNodes = nodes.map((prevNode) => {
        if (prevNode.id === params.target) {
          const currentResponse = prevNode.data.user_response || '';
          const cleanedResponse = currentResponse.startsWith('*')
            ? currentResponse.substring(1).trim()
            : currentResponse;
          const optionExists = cleanedResponse
            .split(' / ')
            .includes(optionLabel);

          let newResponse = cleanedResponse
            ? cleanedResponse + ' / ' + optionLabel
            : optionLabel;

          newResponse = newResponse.replace(/\/\s*\/+/g, '/'); // Eliminar barras dobles

          return optionExists
            ? prevNode
            : {
                ...prevNode,
                data: {
                  ...prevNode.data,
                  user_response: newResponse,
                },
              };
        }
        return prevNode;
      });
      setNodes(newNodes);
      return newNodes;
    } else {
      console.warn(`No matching option found for handle: ${sHandle}`);
      return nodes;
    }
  };

  const removeDataFromChildToParent = (params) => {
    if (!params || !params.sourceHandle || !params.target) {
      console.error('Invalid parameters', params);
      return;
    }

    const sHandle = params.sourceHandle.replace(/^source-/, '');

    const optionLabel = optionLabelFromHandle(params.sourceHandle);

    if (optionLabel) {
      const newNodes = nodes.map((prevNode) => {
        if (prevNode.id === params.target) {
          let currentResponse = prevNode.data.user_response || '';

          // Crear una expresión regular que busque el texto y cualquier barra precedente
          const regex = new RegExp(`\\s*/?\\s*\\b${optionLabel}\\b`, 'g');
          const cleanedResponse = currentResponse.replace(regex, '').trim();

          // Limpiar las barras redundantes
          const updatedResponse = cleanedResponse
            .replace(/\s*\/\s*/g, ' / ')
            .replace(/^\s*\/\s*|\s*\/\s*$/g, '')
            .trim();

          return {
            ...prevNode,
            data: {
              ...prevNode.data,
              user_response: updatedResponse,
            },
          };
        }
        return prevNode;
      });
      setNodes(newNodes);
      return newNodes;
    } else {
      console.warn(`No matching option found for handle: ${sHandle}`);
      return nodes;
    }
  };

  const validateTransitionsLength = (source) => {
    const filter = edges.filter((v) => v.source == source);
    return filter.length + 1;
  };

  const getBotDetails = async (botId, { forceRefresh = false } = {}) => {
    if (!botId) {
      return null;
    }

    if (!forceRefresh && botDetailsCache.current[botId]) {
      return botDetailsCache.current[botId];
    }

    try {
      const url = urlAPI_3 + `chatBot/${botId}`;
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);

      if (!response.ok) {
        console.error('Failed to get bot details', response.status);
        return null;
      }

      const result = await response.json();
      botDetailsCache.current[botId] = result;
      return result;
    } catch (error) {
      console.error('Error fetching bot details:', error);
      return null;
    }
  };

  // CRUD
  //#--> Listar plantillas de bots
  const getBotsTemplates = async () => {
    try {
      const url = urlAPI_3 + 'chatBotTemplates';
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      if (result.length <= 0) {
        setListBotsTemplates([]);
        // setValidation(true);
      } else {
        setListBotsTemplates(result.data);
      }
    } catch (error) {
      console.error('error:', error);
      return null;
    }
  };
  //#--> Listar
  const getBots = async (loader) => {
    try {
      if (loader) {
        setLoading(true);
      }
      const params = new URLSearchParams({ summary: 'true' });
      const url = urlAPI_3 + 'chatBot?' + params.toString();
      botDetailsCache.current = {};
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      if (result.length <= 0) {
        setListBots([]);
        setValidation(true);
      } else {
        setListBots(result);
        setValidation(false);
      }
    } catch (error) {
      console.error('error:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };
  //#--> Crear Bot
  const createBot = async (values, graph) => {
    try {
      setLoading(true);

      setModalCreate(false);
      setMode('create');

      if (graph) {
        setNodes(graph.nodes);
        setEdges(graph.edges);
      }
      let dataBot = await buildJson('create', graph ? graph.edges : edges, graph ? graph.nodes : nodes);

      if (values) {
        setNameBot(values.nameBot);
        setDescriptionBot(values.descriptionBot);
        setBotType(values.botType || 'linear');
        if (!dataBot.name || graph) {
          dataBot.name = values.nameBot;
          dataBot.description = values.descriptionBot;
          dataBot.type = values.botType || 'linear';
        }
      }

      const url = urlAPI_3 + 'chatBot';
      const options = {
        method: 'POST',
        mode: 'cors',
        body: JSON.stringify(dataBot),
      };
      const response = await fetchWithAuth(url, options);
      let result = await response.json();
      if (pendingAgentRef.current && result?._id) {
        pendingAgentRef.current = false;
        const agentConfig = createDefaultAgentConfig();
        try {
          await agentApi.save(result._id, agentConfig);
          result = { ...result, agentConfig };
          agentOpenOnLoadRef.current = true;
        } catch (agentError) {
          sendToast({ type: 'error', message: 'El bot se creó, pero no se pudo activar el Agente IA.' });
        }
      }
      setEditFlow(result);
      lastSavedGraph.current = graphSnapshot(result);
      botRev.current = typeof result?.rev === 'number' ? result.rev : null;

      reset();
      await getBots();
      sendToast({type: "success", message: "Bot creado exitosamente."});
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  //#--> Agregar Nuevo nodo
  const addNewNode = async (data, newNodes, newNode) => {
    setSelectedNode(newNode);
    data.nodes = newNodes;
    const url = urlAPI_3 + `chatBot/${data._id}`;
    const options = {
      method: 'PUT',
      mode: 'cors',
      body: JSON.stringify(data),
    };
    await fetchWithAuth(url, options);

    getBots(false);
  };
  //#--> Duplicar nodo

  
  //#--> Remover un nodo
  const removeNode = async (id) => {
    const data = {
      isFetch: false,
      fetch: {},
      title: '¿Estas seguro que deseas eliminar este nodo?',
      text: 'No se podrá revertir esta acción.',
      icon: 'warning',
      buttons: {
        success: 'Confirmar',
        cancel: 'Cancelar',
      },
    };
    const result = await questionToast(data, setLoading);

    setLoading(true);

    const logsObject = {
      id: editFlow._id,
      nodes: nodes,
      edges: edges,
      flows: editFlow.flows,
    };
    commitHistory(nodes, edges);
    const newNodes = nodes.filter((v) => v.id !== id);
    const newEdges = edges.filter((e) => e.source !== id && e.target !== id);
    setNodes(newNodes);
    setEdges(newEdges);

    setTimeout(async () => {
      await updateBot(editFlow, newEdges, newNodes);

      setSelectedNode(null);
      await getBots(false);
      setLoading(false);
      await createLogs(logsObject);
    }, 500);
  };
  const makeStartNode = async (nodeId) => {
    const target = nodes.find((n) => n.id === nodeId);
    if (!target || target.data?.first || !canBeStartNode(target)) return;

    const currentStart = nodes.find((n) => n.data?.first);
    if (currentStart?.data?.advanced?.campaign?.id === true) {
      sendToast({
        type: 'warning',
        message:
          'El nodo de inicio actual tiene campañas activas. Desactívalas antes de cambiar el inicio.',
      });
      return;
    }

    const hasIncomingEdges = edges.some(
      (e) =>
        e.target === nodeId &&
        !String(e.targetHandle || '').includes('target-return')
    );
    if (hasIncomingEdges) {
      sendToast({
        type: 'warning',
        message:
          'Desconecta las conexiones que llegan a este nodo antes de marcarlo como inicio.',
      });
      return;
    }

    await questionToast(
      {
        isFetch: false,
        fetch: {},
        title: '¿Marcar este nodo como inicio?',
        text:
          'Las conversaciones nuevas empezarán en este nodo; las que están en curso no cambian.\n\n' +
          `"${currentStart?.data?.title || 'El nodo de inicio actual'}" pasará a ser un nodo normal: conéctalo debajo del nuevo inicio para que siga funcionando.`,
        icon: 'warning',
        buttons: {
          success: 'Confirmar',
          cancel: 'Cancelar',
        },
      },
      setLoading
    );

    setLoading(true);
    const logsObject = {
      id: editFlow._id,
      nodes: nodes,
      edges: edges,
      flows: editFlow.flows,
    };
    commitHistory(nodes, edges);

    const newNodes = nodes.map((n) => {
      if (n.id === nodeId) {
        return {
          ...n,
          data: {
            ...n.data,
            first: true,
            trigger_type: 'message',
            flow_template_id: null,
            flow_template_name: null,
          },
        };
      }
      if (n.data?.first) {
        const { first, ...data } = n.data;
        return { ...n, data };
      }
      return n;
    });

    setSelectedNode(null);
    setNodes(newNodes);

    setTimeout(async () => {
      await updateBot(editFlow, edges, newNodes);
      setLoading(false);
      await createLogs(logsObject);
      sendToast({ type: 'success', message: 'Nodo de inicio actualizado.' });
    }, 500);
  };
  //#--> Actualizar BOT
  const putWholeBot = async (data) => {
    const url = urlAPI_3 + `chatBot/${data._id}`;
    const options = {
      method: 'PUT',
      mode: 'cors',
      body: JSON.stringify(data),
    };
    const response = await fetchWithAuth(url, options);
    const result = await response.json();
    if (!response.ok || result?.error) {
      throw new Error(result?.error || 'No se pudo guardar el bot.');
    }
    return result;
  };

  const patchGraph = async (botId, diff) => {
    const url = urlAPI_3 + `chatBot/${botId}/graph`;
    const options = {
      method: 'PATCH',
      mode: 'cors',
      body: JSON.stringify({ rev: botRev.current, ...diff }),
    };
    const response = await fetchWithAuth(url, options);
    const result = await response.json();
    if (response.status === 409 || result?.conflict) {
      return { conflict: true };
    }
    if (!response.ok || result?.error) {
      return null;
    }
    return result;
  };

  const updateBot = async (data, newEdges, newNodes) => {
    try {
      const dataBot = await buildJson('update', newEdges, newNodes);

      data.flows = dataBot.flows;
      data.nodes = dataBot.nodes;
      data.edges = dataBot.edges;

      const nextGraph = {
        nodes: data.nodes,
        edges: data.edges,
        flows: data.flows,
      };
      const baseline = lastSavedGraph.current;
      const diff = baseline ? buildGraphDiff(baseline, nextGraph) : null;

      if (diff && diffIsEmpty(diff)) {
        setEditFlow({ ...data });
        return;
      }

      let result = null;

      if (diff && data._id && diffRatio(diff, nextGraph) <= 0.4) {
        result = await patchGraph(data._id, diff);

        if (result?.conflict) {
          sendToast({
            type: 'error',
            message:
              'El bot fue modificado desde otra sesión. Recargá antes de guardar para no pisar esos cambios.',
          });
          return;
        }
      }

      if (!result) {
        result = await putWholeBot(data);
      }

      if (typeof result?.rev === 'number') {
        botRev.current = result.rev;
      }
      lastSavedGraph.current = graphSnapshot(nextGraph);
      setEditFlow({ ...data });
    } catch (error) {
      console.error(error);
      sendToast({
        type: 'error',
        message: 'No se pudo guardar el bot. Intentá de nuevo.',
      });
    }
  };
  //#--> Listar Logs
  const getLogs = async () => {
    try {
      const url = urlAPI_3 + `chatBot/logs/${editFlow._id}`;
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      let result = await response.json();
      if (result.length <= 0) {
        setListLogs([]);
      } else {
        result = result.sort(
          (a, b) => new Date(b.created) - new Date(a.created)
        );
        setListLogs(result);
      }
    } catch (error) {
      console.error('error:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };
  //#--> Crear Logs
  const createLogs = async (params) => {
    try {
      const dataLogs = {
        chatBotId: params.id,
        nodes: params.nodes,
        edges: params.edges,
        flows: params.flows,
      };

      const url = urlAPI_3 + 'chatBot/logs';
      const options = {
        method: 'POST',
        mode: 'cors',
        body: JSON.stringify(dataLogs),
      };
      const response = await fetchWithAuth(url, options);
      await response.json();
      await getLogs();
    } catch (error) {
      console.error(error);
    }
  };
  //#--> Remover Bot
  // #--> Armar JSON
  const buildJson = async (type, newEdges, newNodes) => {
    newEdges = cleanEdges(newEdges, newNodes);

    let flows = [];
    for (let i in newNodes) {
      const node = newNodes[i];
      let newFlow = {};

      // Transitions
      const filterEdge = newEdges.filter((v) => v.source == node.id);
      const transitions = filterEdge.reduce(
        (acc, v) => {
          if (!acc.seen.has(v.target)) {
            acc.transitions.push({
              next_flow_id: v.target,
              conditions: [],
            });
            acc.seen.add(v.target);
          }
          return acc;
        },
        { transitions: [], seen: new Set() }
      ).transitions;

      // Triggers
      let triggers = [];
      const seenTriggers = new Set();

      if (node.data.first) {
        if (!seenTriggers.has('*')) {
          triggers.push({
            type: 'message',
            pattern: '*',
          });
          seenTriggers.add('*');
        }
      } else if (node.data.user_response) {
        const triggerType = node.data.trigger_type || 'message';
        const userResponseArray = node.data.user_response.includes(' / ')
          ? node.data.user_response.split(' / ')
          : [node.data.user_response];

        const uniquePatterns = new Set(
          userResponseArray.map((item) => item.trim())
        );
        uniquePatterns.forEach((pattern) => {
          const finalPattern = pattern.trim() || '*';

          if (!seenTriggers.has(finalPattern)) {
            const triggerPayload = {
              type: triggerType,
              pattern: finalPattern,
            };

            if (triggerType === 'flows' && node.data?.flow_template_id) {
              triggerPayload.templateId = String(node.data.flow_template_id);
            }

            triggers.push(triggerPayload);
            seenTriggers.add(finalPattern);
          }
        });
      }

      if (node.type == 'basicAnswer') {
        let actions = [];
        if (canUseSticker(node.data) && isStickerUrl(node.data.bot_response)) {
          actions.push(buildStickerAction(node.data.bot_response));
        } else if (node.data.options.length || node.data?.advanced?.dynamic_buttons) {
          actions.push({
            type: 'send_message',
            typeMessage: 'interactive',
            content: {
              options: node.data.options,
              message: node.data.bot_response,
            },
          });
        } else if (node.data?.advanced?.location) {
          actions.push({
            type: 'send_message',
            typeMessage: 'interactive_location',
            content: {
              message: node.data.bot_response,
            },
          });
        } else if (node.data?.advanced?.request_contact) {
          actions.push({
            type: 'send_message',
            typeMessage: 'interactive_contact',
            content: {
              message: node.data.bot_response,
            },
          });
        } else if (node.data?.advanced?.url_button_url) {
          actions.push({
            type: 'send_message',
            typeMessage: 'interactive_url',
            content: {
              url: node.data.advanced.url_button_url,
              buttonText: node.data.advanced.url_button_text,
              message: node.data.bot_response,
            },
          });
        } else if (node.data.bot_response && node.data.file_url_variable) {
          // Usar variable con URL de archivo
          const fileName = node.data.file_url_variable.includes('{{') 
            ? 'media_file' 
            : node.data.file_url_variable.split('/').pop() || 'media_file';
          
          actions.push({
            type: 'send_message',
            typeMessage: 'media',
            content: {
              name: fileName,
              type: node.data.file_type_variable?.value || 'image/jpeg',
              url: node.data.file_url_variable,
              fileText: node.data.bot_response,
            },
          });
        } else if (node.data.bot_response && node.data.file) {
          actions.push({
            type: 'send_message',
            typeMessage: 'media',
            content: {
              name: node.data.file.name,
              type: node.data.file.type,
              url: node.data.file.url,
              fileText: node.data.bot_response,
            },
          });
        } else if (node.data.file_url_variable) {
          // Solo variable sin texto
          const fileName = node.data.file_url_variable.includes('{{') 
            ? 'media_file' 
            : node.data.file_url_variable.split('/').pop() || 'media_file';
          
          actions.push({
            type: 'send_message',
            typeMessage: 'media',
            content: {
              name: fileName,
              type: node.data.file_type_variable?.value || 'image/jpeg',
              url: node.data.file_url_variable,
              fileText: '',
            },
          });
        } else if (node.data.bot_response && !node.data.file) {
          actions.push({
            type: 'send_message',
            typeMessage: 'text',
            content: node.data.bot_response,
          });
        } else if (node.data.file) {
          actions.push({
            type: 'send_message',
            typeMessage: 'media',
            content: {
              name: node.data.file.name,
              type: node.data.file.type,
              url: node.data.file.url,
              fileText: '',
            },
          });
        }

        const moreTriggers = findMoreTrigger(newEdges, newNodes, node.id);
        moreTriggers.forEach((trigger) => {
          if (!seenTriggers.has(trigger.pattern)) {
            triggers.push(trigger);
            seenTriggers.add(trigger.pattern);
          }
        });

        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: actions,
          transitions: transitions,
        };
      }
      if (node.type == 'transferAgent') {
        let type_transfer;
        let content;
        if (node.data?.agent_area?.type == 'agent') {
          type_transfer = 'transfer_agent';
          content = node.data?.agent_area?.value;
        }
        if (node.data?.agent_area?.type == 'area') {
          type_transfer = 'transfer_area';
          content = node.data?.agent_area?.label;
        }
        if (node.data?.agent_area?.type == 'automatic') {
          type_transfer = 'transfer_automatic';
          content = node.data?.agent_area?.id;
        }
        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: [
            isStickerUrl(node.data.bot_response)
              ? buildStickerAction(node.data.bot_response)
              : {
                  type: 'send_message',
                  typeMessage: 'text',
                  content: node.data.bot_response,
                },
            {
              type: type_transfer,
              content: content,
            },
          ],
          transitions: transitions,
        };
      }
      if (node.type == 'tags') {
        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: [
            {
              type: 'taged',
              content: node.data.tags,
            },
          ],
          transitions: transitions,
        };
      }
      if (node.type == 'saveAnswer') {
        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: [
            {
              type: 'saveAnswer',
              content: node.data.variable,
            },
          ],
          transitions: transitions,
        };
      }
      if (node.type == 'flowsWhatsApp') {
        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: [
            {
              type: 'send_flow',
              content: {
                flow: node.data.flow,
                flow_data: node.data.flow_data,
              },
            },
          ],
          transitions: transitions,
        };
      }
      if (node.type == 'queryApi') {
        let actions = [];
        
        if (node.data.bot_response && node.data.bot_response.trim()) {
          actions.push(
            isStickerUrl(node.data.bot_response)
              ? buildStickerAction(node.data.bot_response)
              : {
                  type: 'send_message',
                  typeMessage: 'text',
                  content: node.data.bot_response,
                }
          );
        }

        actions.push({
          type: 'queryApi',
          content: node.data,
        });
        
        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: actions,
          transitions: transitions,
        };
      }
      if (node.type == 'assistantAI') {
        const assistantActions = [];
        if (String(node.data.bot_response ?? '').trim() !== '') {
          assistantActions.push(
            isStickerUrl(node.data.bot_response)
              ? buildStickerAction(node.data.bot_response)
              : {
                  type: 'send_message',
                  typeMessage: 'text',
                  content: node.data.bot_response,
                }
          );
        }
        assistantActions.push({
          type: 'assistantAI',
          content: node.data.assistant,
        });

        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: assistantActions,
          transitions: transitions,
        };
      }
      if (node.type == 'conditions') {
        let sendMessageAction = null;
        if (node.data?.control_message) {
          if (isStickerUrl(node.data.control_message)) {
            sendMessageAction = buildStickerAction(node.data.control_message);
          } else {
            sendMessageAction = {
              type: 'send_message',
              typeMessage: 'text',
              content: node.data.control_message,
            };
          }
        }

        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: [
            ...(sendMessageAction ? [sendMessageAction] : []),
            {
              type: 'conditions',
              content: node.data.conditions,
            },
          ],
          transitions: transitions,
        };
      }
      if (node.type == 'json') {
        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: [
            {
              type: 'json',
              content: node.data.json,
            },
          ],
          transitions: transitions,
        };
      }
      if (node.type == 'webHook') {
        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: [
            {
              type: 'webHook',
              content: null,
            },
          ],
          transitions: transitions,
        };
      }
      if (node.type == 'template') {
        newFlow = {
          id: node.id,
          name: node.data.title,
          triggers: triggers,
          actions: [
            {
              type: 'send_message',
              typeMessage: 'template',
              content: {
                id: node.data.template_id,
                parametersBody: node.data.parametersBody || [],
                parametersMedia: node.data.parametersMedia || {},
                parameterUrl: node.data.parameterUrl || null,
              },
            },
          ],
          transitions: transitions,
        };
      }

      flows.push(newFlow);
    }

    flows = validateFlowsTags(flows);
    flows = validateFlowsSaveAnswer(flows);

    const startNodeId = newNodes.find((n) => n.data?.first)?.id;
    const startIndex = flows.findIndex((f) => f.id === startNodeId);
    if (startIndex > 0) {
      flows = [flows[startIndex], ...flows.filter((_, i) => i !== startIndex)];
    }

    let json = {
      name: nameBot,
      description: descriptionBot,
      type: botType,
      enabled: false,
      inactivityTime: 14,
      inactivityTimeClose: 15,
      inactivityStatus: false,
      companySchedule: '24h',
      flows: flows,
      nodes: nodes,
      edges: edges,
    };

    if (type == 'create') {
      return json;
    } else {
      return {
        flows: flows,
        nodes: newNodes,
        edges: newEdges,
      };
    }
  };
  //#--> Buscar Bot
  const handleSearch = async (searchTerm = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ summary: 'true' });
      if (searchTerm) {
        params.set('search', searchTerm);
      }
      const url = urlAPI_3 + 'chatBot?' + params.toString();
      botDetailsCache.current = {};
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      if (result.length <= 0) {
        setListBots([]);
        setValidation(true);
      } else {
        setListBots(result);
        setValidation(false);
      }

    } catch (error) {
      console.error('Error en la búsqueda:', error);
    } finally {
      setLoading(false);
    }
  };
  //#--> Crear Bot con IA
  const createBotWithIA = async (values) => {
    try {
      setLoading(true);
      setPercentBar(0);
      setProgressMessage('Inicializando parámetros...');

      const data = {
        message: values.instructions_bot,
        nameBot: values.name_bot_ai,
        descriptionBot: values.description_bot_ai,
        botType: values.bot_type_ai || 'linear',
      };

      const url = urlAPI_3 + 'chatBot/newAI';
      const options = {
        method: 'POST',
        mode: 'cors',
        body: JSON.stringify(data),
      };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      const processCode = result.processCode;
      setLoading(false);
      setModalBotAssistant(false);
      setLoadingPercentBar(true);

      let count_limit = 50;
      const statusMessages = [
        'Validando datos...',
        'Optimizando interacción...',
        'Procesando nodos...',
        'Procesando Conectores...',
        'Alineando validaciones...',
        'Finalizando tareas...',
        'Validaciones finales...',
      ];

      let messageIndex = 0;
      let callCount = 0;

      const updateStatusMessage = () => {
        callCount++;

        if (callCount % 2 === 0 && messageIndex < statusMessages.length) {
          setProgressMessage(statusMessages[messageIndex]);
          messageIndex++;
        }
      };

      const updateProgressBar = (increment) => {
        setPercentBar((prev) => {
          const newPercent = prev + increment;
          return newPercent > 100 ? 99 : Math.floor(newPercent);
        });
      };

      const finalizeProgressBar = () => {
        const interval = setInterval(() => {
          setPercentBar((prev) => {
            const newPercent = prev + 5;
            if (newPercent >= 100) {
              clearInterval(interval);
              setLoadingPercentBar(false);
              return 100;
            }
            return newPercent;
          });
        }, 50);
      };

      const checkProcess = async () => {
        count_limit -= 1;
        if (!count_limit) {
          finalizeProgressBar();
          setLoadingPercentBar(false);
          sendToast({
            type: 'error',
            message: 'Hubo un problema al comprobar el estado.',
          });
          return;
        }

        const checkUrl = urlAPI_3 + `chatBotcheckProcces/${processCode}`;
        const options = { method: 'GET', mode: 'cors' };
        const checkResponse = await fetchWithAuth(checkUrl, options);

        if (checkResponse.ok) {
          const checkResult = await checkResponse.json();
          let process = checkResult.process;

          updateProgressBar(Math.random() * 10);
          updateStatusMessage();

          if (process.status === 'completed') {
            reset();
            updateBot(
              process?.detail?.payload,
              process?.detail?.payload?.edges,
              process?.detail?.payload?.nodes
            );
            await getBots();

            finalizeProgressBar();
            sendToast({
              type: 'success',
              message: 'Bot creado exitosamente.',
            });

            return;
          } else if (process.status === 'pending') {
            setTimeout(checkProcess, 3000);
          } else {
            finalizeProgressBar();
            setLoadingPercentBar(false);
            sendToast({
              type: 'error',
              message: 'Hubo un error inesperado.',
            });
            return;
          }
        } else {
          finalizeProgressBar();
          setLoadingPercentBar(false);
          sendToast({
            type: 'error',
            message: 'Hubo un problema al comprobar el estado.',
          });
        }
      };

      setTimeout(checkProcess, 3000);
    } catch (error) {
      console.error(error);
      sendToast({
        type: 'error',
        message: 'Hubo un error inesperado.',
      });
      setLoadingPercentBar(false);
    }
  };

  // #--> ResetData
  const resetData = () => {
    setNameBot('');
    setDescriptionBot('');
    setBotType('linear');
    setNodes([]);
    setEdges([]);
    setRequestResponse({});
    setEditFlow({});
    lastSavedGraph.current = null;
    botRev.current = null;
    setSelectedNode(null);
    setErrorMonitoring(false);
    setSelectedLog(false);
    setShowLogs(false);
    setShowPreview(false);
  };
  // #--> Update name bot
  const updateNameBot = async (bot) => {
    let json = {
      ...bot,
      name: editName,
    };

    setLoading(true);
    const url = urlAPI_3 + `chatBot/${json._id}`;
    const options = {
      method: 'PUT',
      mode: 'cors',
      body: JSON.stringify(json),
    };
    await fetchWithAuth(url, options);

    setLoading(false);
    setShowEditName(false);
    setNameBot(editName);
    setEditName('');
  };
  const validateFlowsTags = (flows) => {
    let tagFlow = null;
    let updatedFlowId = null;

    // Buscar el flujo con 'tags' en su id
    for (let flow of flows) {
      if (flow?.id?.includes('tags')) {
        tagFlow = flow;
        break;
      }
    }

    if (!tagFlow) {
      return flows; // No se encontró el flujo de tags, devolvemos el array original
    }

    // Si `transitions` no está vacío
    if (tagFlow.transitions && tagFlow.transitions.length > 0) {
      const nextFlowId = tagFlow.transitions[0].next_flow_id;

      // Buscar el siguiente flujo por `next_flow_id`
      for (let flow of flows) {
        if (flow.id === nextFlowId) {
          // Concatenar `actions` del siguiente flujo
          flow.actions = flow.actions.concat(tagFlow.actions);
          updatedFlowId = flow.id;
          break;
        }
      }

      // Eliminar solo el flujo de 'tags' específico
      flows = flows.filter((flow) => flow.id !== tagFlow.id);
    }

    // Actualizar las transiciones de los otros flujos si contienen el id del flujo eliminado
    if (updatedFlowId) {
      for (let flow of flows) {
        if (flow.transitions) {
          for (let transition of flow.transitions) {
            if (transition.next_flow_id === tagFlow.id) {
              transition.next_flow_id = updatedFlowId;
            }
          }
        }
      }
    }

    return flows;
  };
  const validateFlowsSaveAnswer = (flows) => {
    let saveAnswerFlow = null;
    let updatedFlowId = null;

    // Buscar el flujo con 'saveAnswer' en su id
    for (let flow of flows) {
      if (flow.id.includes('saveAnswer')) {
        saveAnswerFlow = flow;
        break;
      }
    }

    if (!saveAnswerFlow) {
      return flows; // No se encontró el flujo de saveAnswer, devolvemos el array original
    }

    // Si `transitions` no está vacío
    if (saveAnswerFlow.transitions && saveAnswerFlow.transitions.length > 0) {
      const nextFlowId = saveAnswerFlow.transitions[0].next_flow_id;

      // Buscar el siguiente flujo por `next_flow_id`
      for (let flow of flows) {
        if (flow.id === nextFlowId) {
          // Concatenar `actions` del siguiente flujo
          flow.actions = flow.actions.concat(saveAnswerFlow.actions);
          updatedFlowId = flow.id;
          break;
        }
      }

      // Eliminar solo el flujo de 'saveAnswer' específico
      flows = flows.filter((flow) => flow.id !== saveAnswerFlow.id);
    }

    // Actualizar las transiciones de los otros flujos si contienen el id del flujo eliminado
    if (updatedFlowId) {
      for (let flow of flows) {
        if (flow.transitions) {
          for (let transition of flow.transitions) {
            if (transition.next_flow_id === saveAnswerFlow.id) {
              transition.next_flow_id = updatedFlowId;
            }
          }
        }
      }
    }

    return flows;
  };
  const findMoreTrigger = (edges, nodes, nodeId) => {
    const filterEdges = edges.filter(
      (edge) =>
        edge.target == nodeId && edge.targetHandle.includes('target-return')
    );
    let triggers = [];
    if (filterEdges.length) {
      filterEdges.forEach((edge) => {
        const findNode = nodes.find((node) => node.id == edge.source);
        if (findNode) {
          let sHandle = edge.sourceHandle;
          sHandle = sHandle.replace(/source-/, '');
          sHandle = sHandle.replace(/-\d+$/, '');
          const findOption = (findNode.data?.options || []).find(
            (opt) => opt.id == sHandle
          );

          if (findOption) {
            triggers.push({
              type: 'message',
              pattern: findOption.label,
            });
          }
        }
      });
    }
    return triggers;
  };
  const handlEditChatBot = async (botSummary) => {
    setLoading(true);
    try {
      const fullBot = await getBotDetails(botSummary._id, { forceRefresh: true });
      if (!fullBot) {
        sendToast({
          type: 'error',
          message: 'No fue posible cargar la información del bot.',
        });
        return;
      }

      setMode('create');
      setNodes(fullBot.nodes);
      setEdges(fullBot.edges);
      setEditFlow(fullBot);
      lastSavedGraph.current = graphSnapshot(fullBot);
      botRev.current = typeof fullBot.rev === 'number' ? fullBot.rev : null;
      setNameBot(fullBot.name);
      setDescriptionBot(fullBot.description);
      setBotType(fullBot.type || 'linear');
    } catch (error) {
      console.error('Error cargando detalles del bot:', error);
      sendToast({
        type: 'error',
        message: 'Ocurrió un error al cargar el bot. Intenta nuevamente.',
      });
    } finally {
      setLoading(false);
    }
  };
  const handleOpenNewBot = (initialNodes) => {
    pendingAgentRef.current = false;
    setNodes(initialNodes);
    setModalCreate(true);
  };
  const handleOpenNewAgentBot = () => {
    pendingAgentRef.current = true;
    setNodes([]);
    setEdges([]);
    setModalCreate(true);
  };
  const handleCreateFromModal = ({ values, graph, agent }) => {
    pendingAgentRef.current = Boolean(agent);
    return createBot(values, graph);
  };
  const handleNewBot = (item) => {
    let name = item.name;
    const filter = listBots.filter((v) => v.name.includes(name));
    if (filter.length) {
      name = name + ' ' + (filter.length + 1);
    }

    setNameBot(name);
    setDescriptionBot(item.description);
    setNodes(item.nodes);
    setEdges(item.edges);
    setShowModalCreateTemplate(true);
  };

  // RESOURCES
  const getAreas = async () => {
    try {
      const url = urlAPI_2 + 'getAreasByCompany';
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      if (result.code === 0) {
        sendToast(result);
        setListAreas([]);
      } else {
        const opts = result.data.map((v) => {
          return {
            label: v.description,
            value: v._id,
            type: 'area',
          };
        });
        setListAreas(opts);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const getAgents = async () => {
    try {
      const url = urlAPI_2 + 'usersbyArea';
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      if (result.code === 0) {
        sendToast(result);
      } else if (
        result.message === 'Tu petición no tiene cabecera de autorización'
      ) {
        sendToast({
          type: 'info',
          message:
            'Tu sesion ha caducado, verifica si alguien mas se ha conectado con tus credenciales.',
        });
        navigate('/');
      } else if (result.data.length <= 0) {
        setListAdvisers([]);
      } else {
        const opts = result.data.map((v) => {
          return {
            label: v.name,
            value: v._id,
            type: 'agent',
          };
        });
        setListAdvisers(opts);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const getTags = async () => {
    try {
      const url = urlAPI_1 + `tags`;
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      if (!result.data) {
        setListTags([]);
      } else {
        setListTags(result.data);
      }
    } catch (error) {
      console.error('error:', error);
      return null;
    }
  };
  const getCrmOptions = async () => {
    const load = async (url, pick) => {
      try {
        const response = await fetchWithAuth(url, { method: 'GET', mode: 'cors' });
        const result = await response.json();
        return pick(result) || [];
      } catch (error) {
        return [];
      }
    };
    const [stages, lists] = await Promise.all([
      load(urlAPI_3 + 'stages', (result) => (Array.isArray(result) ? result : result?.data)),
      load(urlAPI_1 + 'lists/options', (result) => result?.data),
    ]);
    setListStages(stages.filter((item) => item?._id && item?.name).map((item) => ({ _id: item._id, name: item.name })));
    setListLists(lists.filter((item) => item?._id && item?.name).map((item) => ({ _id: item._id, name: item.name })));
  };
  const getCorresp = async () => {
    try {
      const url = urlAPI_2 + 'getDataEmail';
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      let data = result.data;
      setCorrespondence(data);
      if (result.message === 'Tu petición no tiene cabecera de autorización') {
        sendToast({
          type: 'info',
          message:
            'Tu sesion ha caducado, verifica si alguien mas se ha conectado con tus credenciales.',
        });
        navigate('/');
      }
    } catch (error) {
      console.error(error);
    }
  };
  const getFlows = async () => {
    try {
      const url = urlAPI_2 + 'flows';
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      if (result.type === 'error') {
        sendToast(result);
      } else if (
        result.message === 'Tu petición no tiene cabecera de autorización'
      ) {
        sendToast({
          type: 'info',
          message:
            'Tu sesion ha caducado, verifica si alguien mas se ha conectado con tus credenciales.',
        });
        navigate('/');
      } else if (!result.data) {
        setFlows([]);
      } else {
        setFlows(result.data);
      }
    } catch (error) {
      console.error(error);
    }
  };
  const getAssistants = async () => {
    try {
      const url = urlAPI_3 + 'assistants';
      const options = { method: 'GET', mode: 'cors' };
      const response = await fetchWithAuth(url, options);
      const result = await response.json();
      if (result.type === 'error') {
        sendToast(result);
      } else if (!result.data) {
        setListAssistants([]);
      } else {
        setListAssistants(result.data);
      }
    } catch (error) {
      console.error('error---->', error);
    }
  };
  const getOptionsInactivity = () => {
    let options = [];
    for (let i = 1; i <= 30; i++) {
      options.push({
        label: i + ' min',
        value: i,
      });
    }

    setListInactivity(options);
  };
  const getOptionsAttempts = () => {
    let options = [];
    for (let i = 1; i <= 10; i++) {
      options.push({
        label: i + ' intento/s',
        value: i,
      });
    }

    setListAttempts(options);
  };

  // Estilos de react-select alineados al lenguaje visual de los nodos
  // (NodeTheme). Compartidos por los 11 paneles de edición.
  const buildSelectStyles = (t) => ({
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? t.inputBg : 'transparent',
      color: t.textPrimary,
      '&:hover': {
        backgroundColor: t.inputBg,
        cursor: 'pointer',
      },
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: t.cardBg,
      border: `1px solid ${t.border}`,
      borderRadius: '10px',
      boxShadow: 'none',
      minHeight: '40px',
      '&:hover': { borderColor: t.border },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: t.panelBg,
      border: `1px solid ${t.border}`,
      borderRadius: '12px',
      overflow: 'hidden',
      zIndex: 1300,
    }),
    singleValue: (provided) => ({ ...provided, color: t.textPrimary }),
    input: (provided) => ({ ...provided, color: t.textPrimary }),
    placeholder: (provided) => ({ ...provided, color: t.textTertiary }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: t.inputBg,
      borderRadius: '99px',
    }),
    multiValueLabel: (provided) => ({ ...provided, color: t.textPrimary }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: t.border,
    }),
  });

  const customStyles = buildSelectStyles(NODE_THEMES.light);
  const customStylesDark = buildSelectStyles(NODE_THEMES.dark);

  const requestApiNode = async () => {
    setLoading(true);
    let url = connectionTestDataParams.url;
    let options = {
      method: connectionTestDataParams.protocol,
      headers: {},
    };

    if (connectionTestData?.headers?.length) {
      options.headers = connectionTestData.headers.reduce((acc, header) => {
        acc[header.key] = header.value;
        return acc;
      }, {});
    }

    if (selectedNode.data.protocol.id !== 'GET') {
      options.body = JSON.stringify({});
      if (connectionTestData?.data?.length) {
        options.body = JSON.stringify(
          connectionTestData.data.reduce((acc, param) => {
            acc[param.key] = param.value;
            return acc;
          }, {})
        );
      }
    }

    if (connectionTestData?.params?.length) {
      const params = new URLSearchParams();
      connectionTestData.params.forEach((param) => {
        params.append(param.key, param.value);
      });
      url += `?${params.toString()}`;
    }

    try {
      const response = await fetch(url, options);

      if (!response.ok) {
        throw new Error('Error en la respuesta de la consulta');
      }

      const blob = await response.blob();
      let result;

      try {
        const text = await blob.text();
        result = JSON.parse(text);

        const hasJson = findJsonNode(selectedNode.id);
        if (!hasJson) {
          setSelectedNode((node) => {
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                variables: [{ ...result, sourceNode: selectedNode.id }],
                response_api: null,
              },
            };

            return updatedNode;
          });
        } else {
          const jsonExpr = hasJson?.data?.json;
          const pathToData = jsonExpr
            ? extractPathFromJsonExpression(jsonExpr)
            : null;
          const extractedData = pathToData
            ? accessNestedValue(result, pathToData)
            : null;

          const variables = [
            {
              ...(extractedData ?? result),
              sourceNode: selectedNode.id,
            },
          ];

          setSelectedNode((node) => {
            const updatedNode = {
              ...node,
              data: {
                ...node.data,
                variables,
                response_api: result,
              },
            };

            return updatedNode;
          });
        }

        const res = convertToTreeResToNode(result, 'response');
        setListOptionsResToNode([res]);
      } catch (e) {
        result = await blob.text();
      }

      if (!result || (Array.isArray(result) && result.length <= 0)) {
        sendToast({
          type: 'info',
          message:
            'Ocurrió un error al realizar la consulta, por favor valida los datos necesarios para realizar la consulta',
        });
      } else {
        setRequestResponse(result);
      }
    } catch (error) {
      console.error('error: ', error);
      sendToast({
        type: 'info',
        message:
          'Ocurrió un error al realizar la consulta, por favor valida los datos necesarios para realizar la consulta',
      });
    } finally {
      setLoading(false);
    }
  };
  let treeIdCounter = 0;
  const getSafeTreeId = (key) => {
    if (typeof key === 'string' && key && key !== 'undefined') return key;
    treeIdCounter += 1;
    return `tree_auto_id_${treeIdCounter}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  };
  const convertToTree = (item, key) => {
    const safeKey = getSafeTreeId(key);
    if (item === null || item === undefined) {
      return { id: safeKey, label: String(key) };
    }

    if (Array.isArray(item)) {
      const children = item.map((subItem, index) =>
        convertToTree(subItem, `${safeKey}[${index}]`)
      );
      return { id: safeKey, label: String(key), children };
    } else if (typeof item === 'object') {
      const children = Object.keys(item).map((subKey) =>
        convertToTree(item[subKey], `${safeKey}.${subKey}`)
      );
      return { id: safeKey, label: String(key), children };
    } else {
      return { id: safeKey, label: String(key) };
    }
  };
  let treeResIdCounter = 0;
  const getSafeResId = (key, parentKey = '') => {
    if (typeof key === 'string' && key && key !== 'undefined')
      return parentKey ? `${parentKey}.${key}` : key;
    treeResIdCounter += 1;
    return `${
      parentKey ? parentKey + '.' : ''
    }tree_res_auto_id_${treeResIdCounter}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  };
  const convertToTreeResToNode = (item, key, parentKey = '') => {
    const uniqueKey = getSafeResId(key, parentKey);

    if (item === null) {
      return { id: uniqueKey, label: `${key}: null` };
    }

    if (item === undefined) {
      return { id: uniqueKey, label: `${key}: undefined` };
    }

    if (Array.isArray(item)) {
      const children = item.map((subItem, index) =>
        convertToTreeResToNode(subItem, `${key}[${index}]`, uniqueKey)
      );
      return { id: uniqueKey, label: key, children };
    } else if (typeof item === 'object') {
      const children = Object.keys(item).map((subKey) =>
        convertToTreeResToNode(item[subKey], subKey, uniqueKey)
      );
      return { id: uniqueKey, label: key, children };
    } else {
      return { id: uniqueKey, label: `${key}: ${item}` };
    }
  };
  const extractKeys = (item, parentKey = '') => {
    if (item?.variables && Array.isArray(item)) return [];

    const keysList = [];

    const traverse = (obj, currentKey) => {
      if (Array.isArray(obj)) {
        const marker = `{{${currentKey}}}`;
        keysList.push({ label: marker, value: marker });
      } else if (typeof obj === 'object' && obj !== null) {
        Object.keys(obj).forEach((key) => {
          const newKey = currentKey ? `${currentKey}.${key}` : key;
          traverse(obj[key], newKey);
        });
      } else {
        const marker = `{{${currentKey}}}`;
        keysList.push({ label: marker, value: marker });
      }
    };

    traverse(item, parentKey);
    return keysList;
  };
  // El tipo del nodo mapea 1:1 con las claves de nodeTypesColors, así que no hay
  // que ir tipo por tipo (la versión anterior comparaba contra node.id y se
  // olvidaba de 'template', que quedaba sin color en el minimapa).
  const nodeColor = (node) =>
    nodeTypesColors[node.type] || nodeTypesColors['assistantAI'];
  const findJsonNode = (nodeId) => {
    const { flows } = editFlow;

    const flow = flows?.find((f) => f.id === nodeId);
    if (!flow || !Array.isArray(flow.transitions)) return null;

    const jsonTransition = flow.transitions.find(
      (t) =>
        typeof t.next_flow_id === 'string' && t.next_flow_id.includes('json')
    );

    if (!jsonTransition) return null;

    const matchingNode = nodes.find(
      (node) => node.id === jsonTransition.next_flow_id
    );

    return matchingNode || null;
  };
  const extractPathFromJsonExpression = (expression) => {
    const inner = expression.replace(/[{}]/g, '');
    const separatorIndex = inner.indexOf('-');
    if (separatorIndex === -1) return null;

    const remainder = inner.slice(separatorIndex + 1);

    const dotIndex = remainder.indexOf('.');
    return dotIndex !== -1 ? remainder.slice(dotIndex + 1) : remainder;
  };
  const accessNestedValue = (obj, path) => {
    try {
      return path.split('.').reduce((acc, key) => {
        const arrayMatch = key.match(/(\w+)\[(\d+)\]/);
        if (arrayMatch) {
          const prop = arrayMatch[1];
          const index = parseInt(arrayMatch[2], 10);
          return acc?.[prop]?.[index];
        }
        return acc?.[key];
      }, obj);
    } catch {
      return null;
    }
  };

  const handleClickModalVariables = (event, typeValue, type) => {
    setAnchorEl(event.currentTarget);
    setVariablesModal(true);
    setTypeInputValue(typeValue);
    setTypeInput(type);
  };

  const responseToNode = (event) => {
    setAnchorEl2(event.currentTarget);
    setResponseToNodeModal(true);
  };
  const findNodeById = (nodes, id) => {
    for (let node of nodes) {
      if (node.id === id) {
        return node;
      }
      if (node.children) {
        const childResult = findNodeById(node.children, id);
        if (childResult) {
          return childResult;
        }
      }
    }
    return null;
  };
  const convertToNode = () => {
    const position = screenToFlowPosition({ x: 250, y: 15 });

    const newNode = {
      id: getId() + '-basicAnswer',
      type: 'basicAnswer',
      data: {
        title: 'Respues Básica',
        bot_response: listToNodeData.description,
        user_response: '*',
        options: listToNodeData.options,
        file: null,
        variables: null,
        saved: true,
      },
      position,
    };

    setNodes((nds) => [...nds, newNode]);
    let newNodes = [...nodes, newNode];
    setTimeout(() => {
      addNewNode(editFlow, newNodes, newNode);
    }, 500);

    setResponseToNodeModal(false);
    setVariablesOptionsModal(false);
    setListToNodeData({
      options: [],
      description: '',
    });
    setConnectionTestData({
      headers: [],
      params: [],
      data: [],
    });
  };

  // Funcion para manejar vista ventana tools
  const handleErrorMonitoring = (close = false) => {
    if (errorMonitoring) {
      setAnimateClass('slide-out');
      setTimeout(() => {
        setErrorMonitoring(false);
        setAnimateClass('');
      }, 500);
    } else {
      if (!close) {
        setShowPreview(false);
        setShowLogs(false);
        setShowDiagnostics(false);
        setErrorMonitoring(true);
        setAnimateClass('slide-in');
      }
    }
  };

  // Funcion para ir al nodo inicial
  const goToInitialNode = () => {
    const fundNode = nodes.find((v) => v.data?.first);
    if (fundNode) {
      const nodeWidth = fundNode.width || 100;
      const nodeHeight = fundNode.height || 100;

      const nodeBounds = {
        x: fundNode.position.x - nodeWidth / 20,
        y: fundNode.position.y - nodeHeight / 20,
        width: nodeWidth,
        height: nodeHeight,
      };

      fitBounds(nodeBounds, { duration: 1000, padding: 0.1 });
    }
  };
  // Funcion para ir a un nodo
  const goToNode = (paramNode) => {
    const nodeWidth = paramNode.width || 100;
    const nodeHeight = paramNode.height || 100;

    const nodeBounds = {
      x: paramNode.position.x - nodeWidth / 20,
      y: paramNode.position.y - nodeHeight / 20,
      width: nodeWidth,
      height: nodeHeight,
    };

    fitBounds(nodeBounds, { duration: 1000, padding: 0.1 });
  };
  // Funcion para observar nodos desconectados
  const watchDisconnectedEdges = () => {
    const connectedNodes = new Set();

    edges.forEach((edge) => {
      connectedNodes.add(edge.source);
      connectedNodes.add(edge.target);
    });

    const disconnectedNodes = nodes.filter(
      (node) => !connectedNodes.has(node.id)
    );

    setListDisconnectedNodes(disconnectedNodes);
  };
  // Funcion para observar nodos con opciones desconectadas
  const watchDisconnectedOptions = () => {
    const connectedOptions = new Set();
    const optionsToNodesMap = new Map();

    // Buscar todas las opciones en el flujo y mapear nodos
    nodes.forEach((node) => {
      if (node.type === 'basicAnswer' && node.data?.options) {
        node.data.options.forEach((option) => {
          connectedOptions.add(option.id);
          optionsToNodesMap.set(option.id, node);
        });
      }
    });

    const nodesWithDisconnectedOptions = new Set();

    // Buscar todos los edges en el flujo y comparar
    optionsToNodesMap.forEach((node, optionId) => {
      const findEdge = edges.find((edge) =>
        edge.sourceHandle.includes(optionId)
      );
      if (!findEdge) {
        nodesWithDisconnectedOptions.add(node);
      }
    });

    setListDisconnectedOptions(Array.from(nodesWithDisconnectedOptions));
  };
  // Funcion para observar nodos con campos incompletos
  const watchNodesWithErrorInFields = () => {
    const nodesWithErrorInFields = nodes.filter((node) => {
      const missingTrigger = !node.data?.first && !node.data.user_response;
      switch (node.type) {
        case 'basicAnswer':
          return missingTrigger || !node.data.bot_response;
        case 'transferAgent':
          return (
            missingTrigger ||
            !node.data.bot_response ||
            !node.data.agent_area
          );
        case 'tags':
          return missingTrigger || !node.data.tags;
        case 'assitantAI':
          return (
            missingTrigger ||
            !node.data.bot_response ||
            !node.data.assistant
          );
        case 'saveAnswer':
          return missingTrigger || !node.data.variable;
        case 'flowsWhatsApp':
          return (
            missingTrigger ||
            !node.data.flow ||
            !node.data.flow_data?.body ||
            !node.data.flow_data?.footer ||
            !node.data.flow_data?.button
          );
        case 'queryApi':
          return (
            missingTrigger ||
            !node.data.bot_response ||
            !node.data.url_api ||
            !node.data.protocol ||
            !node.data.auth
          );
        case 'json':
          return missingTrigger;
        case 'webHook':
          return missingTrigger;
        default:
          return false;
      }
    });

    setListNodesWithErrorInFields(nodesWithErrorInFields);
  };
  // Funcion para observar flujos con triggers duplicados
  const watchTriggersDuplicates = () => {
    let totalDuplicateCount = 0;

    editFlow?.flows?.forEach((flow) => {
      const triggersMap = new Map();
      flow?.triggers?.forEach((trigger) => {
        const key = `${trigger.type}-${trigger.pattern}`;
        if (triggersMap.has(key)) {
          triggersMap.set(key, triggersMap.get(key) + 1);
        } else {
          triggersMap.set(key, 1);
        }
      });

      triggersMap.forEach((count) => {
        if (count > 1) {
          totalDuplicateCount += count;
        }
      });
    });

    setTriggersDuplicateCount(totalDuplicateCount);
  };
  // Funcion para observar flujos con transitions duplicadas
  const watchTransitionsDuplicates = () => {
    let totalDuplicateCount = 0;

    editFlow?.flows?.forEach((flow) => {
      const transitionsMap = new Map();
      flow.transitions.forEach((transition) => {
        const key = transition.next_flow_id;
        if (transitionsMap.has(key)) {
          transitionsMap.set(key, transitionsMap.get(key) + 1);
        } else {
          transitionsMap.set(key, 1);
        }
      });

      transitionsMap.forEach((count) => {
        if (count > 1) {
          totalDuplicateCount += count;
        }
      });
    });

    setTransitionsDuplicateCount(totalDuplicateCount);
  };
  // Funcion para buscar el color del nodo
  const findNodeColor = (typeNode) => {
    let color = '';
    if (typeNode === 'basicAnswer') {
      color = nodeTypesColors['basicAnswer'];
    }
    if (typeNode === 'transferAgent') {
      color = nodeTypesColors['transferAgent'];
    }
    if (typeNode === 'tags') {
      color = nodeTypesColors['tags'];
    }
    if (typeNode === 'assistantAI') {
      color = nodeTypesColors['assistantAI'];
    }
    if (typeNode === 'saveAnswer') {
      color = nodeTypesColors['saveAnswer'];
    }
    if (typeNode === 'flowsWhatsApp') {
      color = nodeTypesColors['flowsWhatsApp'];
    }
    if (typeNode === 'queryApi') {
      color = nodeTypesColors['queryApi'];
    }
    if (typeNode === 'json') {
      color = nodeTypesColors['json'];
    }
    if (typeNode === 'webHook') {
      color = nodeTypesColors['webHook'];
    }

    return color;
  };
  // Funciona para abrir logs
  const handleShowLogs = async () => {
    if (showLogs) {
      setAnimateClass('slide-out');
      setTimeout(() => {
        setShowLogs(false);
      }, 500);
    } else {
      handleErrorMonitoring(true);
      setShowPreview(false);
      setShowDiagnostics(false);
      setTimeout(() => {
        setShowLogs(true);
        setAnimateClass('slide-in');
      }, 500);
    }
  };
  // Funcion para abrir/cerrar el panel de Diagnóstico
  const handleShowDiagnostics = () => {
    if (showDiagnostics) {
      setAnimateClass('slide-out');
      setTimeout(() => {
        setShowDiagnostics(false);
        setAnimateClass('');
      }, 500);
    } else {
      handleErrorMonitoring(true);
      setShowPreview(false);
      setShowLogs(false);
      setShowDiagnostics(true);
      setAnimateClass('slide-in');
    }
  };
  // Persiste las reglas de diagnóstico en el documento del bot. Reusa el guardado
  // completo (updateBot) con los nodos/edges vivos del canvas, ya que el endpoint
  // de update requiere el payload completo del bot.
  const handleChangeDiagnosticRules = async (nextRules) => {
    const updated = { ...editFlow, diagnosticRules: nextRules };
    setEditFlow(updated);
    await updateBot(updated, edges, nodes);
  };
  // Funciona para seleccionar log
  const selectLog = (params) => {
    setSelectedLog(true);
    setNodes(params.nodes);
    setEdges(params.edges);

    if (errorMonitoring) {
      handleErrorMonitoring(true);
    }
    if (showPreview) {
      handlePreview();
    }
  };
  // Funcion para retornar a version seleccionada de los logs
  const returnVersion = async () => {
    setLoading(true);
    setSelectedLog(false);
    await createLogs({
      nodes: editFlow.nodes,
      edges: editFlow.edges,
      flows: editFlow.flows,
      id: editFlow._id,
    });
    await updateBot(editFlow, edges, nodes);
    setLoading(false);
  };

  const truncateText = (text, maxLength = 26) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;
  };

  // Functions for conditions
  let conditionIdCounter = 0;
  const getConditionId = () => {
    conditionIdCounter += 1;
    return `condition_${conditionIdCounter}_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 6)}`;
  };

  const ensureConditionIds = (conditions = []) => {
    let hasMissing = false;
    const normalized = conditions.map((condition) => {
      if (condition?.id) return condition;
      hasMissing = true;
      return {
        ...condition,
        id: getConditionId(),
      };
    });

    return { normalized, hasMissing };
  };

  const addRule = (type) => {
    setSelectedNode((node) => {
      const { normalized } = ensureConditionIds(node.data.conditions || []);
      const updatedNode = {
        ...node,
        data: {
          ...node.data,
          conditions: normalized,
        },
      };

      if (type === 'and') {
        updatedNode.data?.conditions.push({
          id: getConditionId(),
          type: 'AND',
          rule: {
            value1: null,
            value2: null,
            value3: null,
            operator: null,
            operatorType: null,
          },
        });
      }

      if (type === 'or') {
        updatedNode.data?.conditions.push({
          id: getConditionId(),
          type: 'OR',
          rule: {
            value1: null,
            value2: null,
            value3: null,
            operator: null,
            operatorType: null,
          },
        });
      }

      return updatedNode;
    });
  };

  const getOptionsOperators = () => {
    setListOptionsOperators(
      [
        {
          label: 'Operadores de estados',
          options: [
            {
              label: 'Terminos y condiciones',
              id: 'isAcceptTerms',
              type: 'status',
            },
            {
              label: 'Solicitud de nombre',
              id: 'request_name',
              type: 'status',
            },
            {
              label: 'Confirmar nombre',
              id: 'confirm_name',
              type: 'status',
            },
            {
              label: 'Editar nombre',
              id: 'edit_name',
              type: 'status',
            },
          ],
        },
        {
          label: 'Operadores básicos',
          options: [
            {
              label: 'Existe',
              id: 'exist',
              type: 'basic',
            },
            {
              label: 'No existe',
              id: 'does_not_exist',
              type: 'basic',
            },
          ],
        },
        {
          label: 'Operadores de formato',
          options: [
            {
              label: 'Fecha',
              id: 'date',
              type: 'format',
            },
            {
              label: 'Hora',
              id: 'hour',
              type: 'format',
            },
            {
              label: 'Correo',
              id: 'mail',
              type: 'format',
            },
            {
              label: 'Numérico',
              id: 'number',
              type: 'format',
            },
            {
              label: 'Teléfono',
              id: 'phone',
              type: 'format',
            },
          ],
        },
        {
          label: 'Ventana de actividad (24h)',
          options: [
            {
              label: 'Dentro de las últimas 24h',
              id: 'is_within_24h',
              type: 'window_24h',
            },
            {
              label: 'Fuera de las últimas 24h',
              id: 'is_not_within_24h',
              type: 'window_24h',
            },
          ],
        },
        {
          label: 'Operadores de tipos de mensaje WhatsApp',
          options: [
            {
              label: 'Texto',
              id: 'text_message',
              type: 'whatsapp_message',
            },
            {
              label: 'Imagen',
              id: 'image_message',
              type: 'whatsapp_message',
            },
            {
              label: 'Video',
              id: 'video_message',
              type: 'whatsapp_message',
            },
            {
              label: 'Audio',
              id: 'audio_message',
              type: 'whatsapp_message',
            },
            {
              label: 'Documento',
              id: 'document_message',
              type: 'whatsapp_message',
            },
            {
              label: 'Ubicación',
              id: 'location_message',
              type: 'whatsapp_message',
            },
            {
              label: 'Contacto',
              id: 'contact_message',
              type: 'whatsapp_message',
            },
            {
              label: 'Sticker',
              id: 'sticker_message',
              type: 'whatsapp_message',
            },
          ],
        },
        {
          label: 'Operadores de texto',
          options: [
            {
              label: 'Igual a',
              id: 'equal_to',
              type: 'text',
            },
            {
              label: 'Igual a (No distingue entre mayúscula o minúscula)',
              id: 'equal_to_case_sensative',
              type: 'text',
            },
            {
              label: 'No es igual a',
              id: 'not_equal_to',
              type: 'text',
            },
            {
              label: 'No es igual a (No distingue entre mayúscula o minúscula)',
              id: 'not_equal_to_case_sensative',
              type: 'text',
            },
            {
              label: 'Contiene',
              id: 'contain',
              type: 'text',
            },
            {
              label: 'Contiene (No distingue entre mayúscula o minúscula)',
              id: 'contain_case_sensative',
              type: 'text',
            },
            {
              label: 'No contiene',
              id: 'does_not_contain',
              type: 'text',
            },
            {
              label: 'No contiene (No distingue entre mayúscula o minúscula)',
              id: 'does_not_contain_case_sensative',
              type: 'text',
            },
          ],
        },
        {
          label: 'Operadores númericos',
          options: [
            {
              label: 'Igual a',
              id: 'equal_to',
              type: 'numeric',
            },
            {
              label: 'No es igual a',
              id: 'not_equal_to',
              type: 'numeric',
            },
            {
              label: 'Mayor que',
              id: 'greater_than',
              type: 'numeric',
            },
            {
              label: 'Menor que',
              id: 'less_than',
              type: 'numeric',
            },
            {
              label: 'Mayor que o igual a',
              id: 'greater_than_or_equal_to',
              type: 'numeric',
            },
            {
              label: 'Menor que o igual a',
              id: 'less_than_or_equal_to',
              type: 'numeric',
            },
          ],
        },
        {
          label: 'Operadores de fecha',
          options: [
            {
              label: 'Igual a',
              id: 'equal_to',
              type: 'datatime',
            },
            {
              label: 'No es igual a',
              id: 'not_equal_to',
              type: 'datatime',
            },
            {
              label: 'Más tarde que',
              id: 'later_than',
              type: 'datatime',
            },
            {
              label: 'Más temprano que',
              id: 'earlier_than',
              type: 'datatime',
            },
            {
              label: 'Más tarde o igual a',
              id: 'later_than_or_equal_to',
              type: 'datatime',
            },
            {
              label: 'Más temprano o igual a',
              id: 'earlier_than_or_equal_to',
              type: 'datatime',
            },
          ],
        },
        {
          label: 'Operadores de tiempo',
          options: [
            {
              label: 'Igual a',
              id: 'equal_to',
              type: 'time',
            },
            {
              label: 'No es igual a',
              id: 'not_equal_to',
              type: 'time',
            },
            {
              label: 'Mayor que',
              id: 'greater_than',
              type: 'time',
            },
            {
              label: 'Menor que',
              id: 'less_than',
              type: 'time',
            },
            {
              label: 'Mayor que o igual a',
              id: 'greater_than_or_equal_to',
              type: 'time',
            },
            {
              label: 'Menor que o igual a',
              id: 'less_than_or_equal_to',
              type: 'time',
            },
          ],
        },
        {
          label: 'Operadores de arrays',
          options: [
            {
              label: 'Es un array',
              id: 'is_array',
              type: 'array',
            },
            {
              label: 'No es un array',
              id: 'is_not_array',
              type: 'array',
            },
            {
              label: 'Tiene longitud',
              id: 'has_length',
              type: 'array',
            },
            {
              label: 'Está vacío',
              id: 'is_empty',
              type: 'array',
            },
            {
              label: 'No está vacío',
              id: 'is_not_empty',
              type: 'array',
            },
          ],
        },
        {
          label: 'Operadores de objetos',
          options: [
            {
              label: 'Es un objeto',
              id: 'is_object',
              type: 'object',
            },
            {
              label: 'No es un objeto',
              id: 'is_not_object',
              type: 'object',
            },
            {
              label: 'Tiene propiedades',
              id: 'has_properties',
              type: 'object',
            },
            {
              label: 'Cantidad de propiedades igual a',
              id: 'properties_count_equal_to',
              type: 'object',
            },
            {
              label: 'Está vacío',
              id: 'is_empty',
              type: 'object',
            },
            {
              label: 'No está vacío',
              id: 'is_not_empty',
              type: 'object',
            },
            {
              label: 'Contiene clave',
              id: 'contains_key',
              type: 'object',
            },
            {
              label: 'No contiene clave',
              id: 'does_not_contain_key',
              type: 'object',
            },
          ],
        },
        {
          label: 'Operadores de errores HTTP',
          options: [
            {
              label: '400 - Solicitud incorrecta',
              id: 'bad_request',
              type: 'http_error',
            },
            {
              label: '401 - No autorizado',
              id: 'unauthorized',
              type: 'http_error',
            },
            {
              label: '403 - Prohibido',
              id: 'forbidden',
              type: 'http_error',
            },
            {
              label: '404 - No encontrado',
              id: 'not_found',
              type: 'http_error',
            },
            {
              label: '409 - Conflicto',
              id: 'conflict',
              type: 'http_error',
            },
            {
              label: '422 - Entidad no procesable',
              id: 'unprocessable_entity',
              type: 'http_error',
            },
            {
              label: '429 - Demasiadas solicitudes',
              id: 'too_many_requests',
              type: 'http_error',
            },
            {
              label: '500 - Error interno del servidor',
              id: 'internal_server_error',
              type: 'http_error',
            },
            {
              label: '502 - Puerta de enlace incorrecta',
              id: 'bad_gateway',
              type: 'http_error',
            },
            {
              label: '503 - Servicio no disponible',
              id: 'service_unavailable',
              type: 'http_error',
            },
            {
              label: '504 - Tiempo de espera agotado',
              id: 'gateway_timeout',
              type: 'http_error',
            },
          ],
        },
      ].filter(Boolean)
    );
  };

  const removeCondition = (index) => {
    setSelectedNode((node) => {
      const updatedConditions = node.data.conditions
        ? [...node.data.conditions]
        : [];
      updatedConditions.splice(index, 1);

      return {
        ...node,
        data: {
          ...node.data,
          conditions: updatedConditions,
        },
      };
    });
  };

  const saveConditions = async (values) => {
    if (!selectedNode) {
      return;
    }

    const { normalized } = ensureConditionIds(selectedNode.data.conditions);
    const updatedConditions = normalized.map((condition) => {
      const value1Key = `conditions_${condition.id}_${selectedNode.id}_rule_value1`;
      const value2Key = `conditions_${condition.id}_${selectedNode.id}_rule_value2`;
      const value3Key = `conditions_${condition.id}_${selectedNode.id}_rule_value3`;
      const value1FromForm = values[value1Key];
      const value2FromForm = values[value2Key];
      const value3FromForm = values[value3Key];

      const value1Resolved =
        value1FromForm !== undefined && String(value1FromForm).trim() !== ''
          ? value1FromForm
          : condition.rule.value1;
      const value2Resolved =
        value2FromForm !== undefined && String(value2FromForm).trim() !== ''
          ? value2FromForm
          : condition.rule.value2;
      const value3Resolved =
        value3FromForm !== undefined && String(value3FromForm).trim() !== ''
          ? value3FromForm
          : condition.rule.value3;

      return {
        ...condition,
        rule: {
          ...condition.rule,
          value1: value1Resolved,
          value2: value2Resolved,
          value3: value3Resolved,
        },
      };
    });

    setSelectedNode((node) => ({
      ...node,
      data: {
        ...node.data,
        conditions: updatedConditions,
      },
    }));

    setShowConditionsModal(false);
    reset();
  };

  const handlePreview = () => {
    if (showPreview) {
      setAnimateClass('slide-out');
      setTimeout(() => {
        setShowPreview(false);
      }, 500);
    } else {
      handleErrorMonitoring(true);
      setShowLogs(false);
      setShowDiagnostics(false);
      setTimeout(() => {
        setShowPreview(true);
        setAnimateClass('slide-in');
      }, 500);
    }
  };

  const cleanEdges = (newEdges, newNodes) => {
    const nodeIds = new Set(newNodes.map((node) => node.id));
    const optionIds = new Set(
      newNodes.flatMap((node) =>
        (node.data?.options || []).map((option) => option.id)
      )
    );

    return newEdges.filter((edge) => {
      const match = edge.sourceHandle?.match(/^source-(.*?)(-\d+)?$/);
      const sourceHandleId = match ? match[1] : null;
      if (
        [
          'source-queryApi-succes',
          'source-queryApi-failde',
          'source-conditions-success',
          'source-conditions-failed',
          'campaign_option',
        ].some((keyword) => edge.sourceHandle.includes(keyword))
      ) {
        return nodeIds.has(edge.source) && nodeIds.has(edge.target);
      }

      return (
        nodeIds.has(edge.source) &&
        nodeIds.has(edge.target) &&
        ([...nodeIds].some((v) => v.includes(sourceHandleId)) ||
          [...optionIds].some((v) => v.includes(sourceHandleId)))
      );
    });
  };

  const resetVariable = () => {
    setTypeInputValue(null);
    setTypeInput(null);
    setValueSelectedVariable(null);
  };

  const objectToVariableTree = (
    obj,
    nodeId,
    prefix = '',
    type = 'queryApi',
    parentIdx = ''
  ) => {
    const safeKey = (key, idx) =>
      typeof key === 'string' && key.length > 0 ? key : `item_${idx}`;
  // Eliminado makeId, usaremos el prefijo tal cual para el id
    if (Array.isArray(obj)) {
      let result = [];
      obj.forEach((item, idx) => {
        const children = objectToVariableTree(
          item,
          nodeId,
          prefix ? `${prefix}.${idx}` : `${idx}`,
          type,
          idx
        );
        if (Array.isArray(children)) result = result.concat(children);
        else if (children) result.push(children);
      });
      return result;
    } else if (typeof obj === 'object' && obj !== null) {
      let result = [];
      Object.keys(obj).forEach((key, idx) => {
        const safeK = safeKey(key, idx);
        let newPrefix = '';
        if (prefix) {
          newPrefix = `${prefix}.${safeK}`;
        } else {
          newPrefix = safeK;
        }
        let idPath = newPrefix ? `${nodeId}.${newPrefix}` : `${nodeId}`;
        if (
          !idPath ||
          idPath === 'undefined' ||
          idPath === `${nodeId}-${type}.`
        ) {
          idPath = `${nodeId}-${type}.id_${Math.random()
            .toString(36)
            .slice(2, 10)}`;
        }
        const children = objectToVariableTree(
          obj[key],
          nodeId,
          newPrefix,
          type,
          idx
        );
        if (Array.isArray(children) && children.length > 0) {
          result.push({
            label: safeK,
            id: idPath,
            children: children,
          });
        } else if (children && children.value) {
          result.push({
            label: safeK,
            value: `{{${idPath}}}`,
            id: idPath,
          });
        }
      });
      return result;
    } else {
      let idPath = prefix ? `${nodeId}.${prefix}` : `${nodeId}`;
      return [
        {
          label: prefix || `item_${parentIdx}`,
          value: `{{${idPath}}}`,
          id: idPath,
        },
      ];
    }
  };

  const collectApiData = (key, setter) => {
    const array = [];
    const filter = nodes?.filter(
      (v) => v.type === 'queryApi' || v.type === 'webHook'
    );
    if (filter?.length) {
      filter.forEach((f) => {
        let raw = f.data?.[key];
        
        if (typeof raw === 'string') {
          try {
            raw = JSON.parse(raw);
          } catch (e) {
            console.warn('Error parsing JSON string:', e);
            return;
          }
        }
        
        const isRootArray = Array.isArray(raw);
        const rawArray = isRootArray ? raw : raw ? [raw] : [];
        if (rawArray.length) {
          const tree = objectToVariableTree(
            rawArray[0],
            f.id,
            isRootArray ? '0' : '',
            f.type
          );
          array.push({
            api: f.data?.title,
            id: f.id,
            data: tree,
            type: f.type,
          });
        }
      });
    }
    setter(array);
  };

  const CustomBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      background: '#00A884',
      right: '2px',
      top: '2px',
    },
  }));

  const getDataConditions = () => {
    if (!selectedNode?.data?.conditions || !Array.isArray(selectedNode.data.conditions)) return;
    const { normalized, hasMissing } = ensureConditionIds(selectedNode.data.conditions);
    if (hasMissing) {
      setSelectedNode((node) => ({
        ...node,
        data: {
          ...node.data,
          conditions: normalized,
        },
      }));
    }
    const objectData = {};
    normalized.forEach((condition) => {
      const nodeId = selectedNode.id;
      objectData[`conditions_${condition.id}_${nodeId}_rule_value1`] =
        condition?.rule?.value1 ?? '';
      objectData[`conditions_${condition.id}_${nodeId}_rule_value2`] =
        condition?.rule?.value2 ?? '';
      objectData[`conditions_${condition.id}_${nodeId}_rule_value3`] =
        condition?.rule?.value3 ?? '';
    });
    reset(objectData, { keepDefaultValues: true });
  };

  // Recursos de la empresa: no dependen del bot abierto, se cargan una sola vez.
  // Antes se repetían en cada cambio de modo y cada respuesta re-renderizaba el
  // canvas completo justo mientras se abría el bot.
  useEffect(() => {
    getOptionsInactivity();
    getOptionsAttempts();

    const fetchData = async () => {
      try {
        await Promise.all([
          getAgents(),
          getBotsTemplates(),
          getAreas(),
          getTags(),
          getCrmOptions(),
          getCorresp(),
          getFlows(),
          getAssistants(),
          getOptionsOperators(),
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (editFlow?._id) getLogs();
  }, [editFlow?._id]);

  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        if (selectedNode && node.id === selectedNode.id) {
          node.data = {
            ...selectedNode.data,
          };
        }

        return node;
      })
    );

    if (selectedNode) {
      collectApiData('variables', setContextVariables);
      collectApiData('response_api', setContextResponseApi);
    }
  }, [selectedNode]);

  useEffect(() => {
    watchDisconnectedEdges();
    watchDisconnectedOptions();
    watchNodesWithErrorInFields();
    watchTriggersDuplicates();
    watchTransitionsDuplicates();
  }, [nodes, listBots, edges]);

  useEffect(() => {
    if (showConditionsModal) {
      getDataConditions();
    }
  }, [showConditionsModal]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (nodeContextModal) {
        setNodeContextModal(false);
      }
    };

    if (nodeContextModal) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [nodeContextModal]);

  return (
    <>
      {loading ? (
        <div className="content-spinner">
          <Spinner />
        </div>
      ) : null}

      {loadingPercentBar ? (
        <div
          className="content-spinner"
          style={{
            backgroundColor: isDark === 'false' ? 'rgb(0 0 0 / 80%)' : '',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <DotLottieReact
              src="https://lottie.host/82ab7034-cdf7-4cde-9380-ba82a6d78f55/4FNYQ2JYhZ.lottie"
              loop
              autoplay
              className="lottie-animation pt-1"
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '20px',
                width: '100%',
              }}
            >
              <div
                style={{ marginRight: '14px', width: '90px', height: '90px' }}
              >
                <CircularProgressbar
                  value={percentBar}
                  text={`${percentBar}%`}
                  maxValue={100}
                  minValue={0}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: '#fff',
                  }}
                >
                  Construyendo...
                </span>
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: 'bold',
                    color: '#c9c9c9',
                    width: '190px',
                  }}
                >
                  {progressMessage}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {mode === 'list' && (
        <List
          handleSearch={handleSearch}
          handlEditChatBot={handlEditChatBot}
          handleOpenNewBot={handleOpenNewBot}
          handleOpenNewAgentBot={handleOpenNewAgentBot}
          handleNewBot={handleNewBot}
          handleCreateFromModal={handleCreateFromModal}
          getBots={getBots}
          getBotDetails={getBotDetails}
          validation={validation}
          listBots={listBots}
          listInactivity={listInactivity}
          listCompanyScheduleOptions={listCompanyScheduleOptions}
          listAttempts={listAttempts}
          listAreas={listAreas}
          listAdvisers={listAdvisers}
          listBotsTemplates={listBotsTemplates}
          isDark={isDark}
          customStyles={customStyles}
          customStylesDark={customStylesDark}
          mode={mode}
          handleModalBotAssistant={setModalBotAssistant}
        />
      )}

      {mode === 'create' && (
        <CanvasShell>
            <BotCanvasBar
              name={nameBot}
              onBack={() => {
                setMode('list');
                setFlows([]);
                resetData();
                getBots();
              }}
              onEditName={() => {
                setShowEditName(true);
                setEditName(nameBot);
              }}
              readOnly={Boolean(selectedLog)}
              onRestoreBack={() => {
                setNodes(editFlow.nodes);
                setEdges(editFlow.edges);
                setSelectedLog(false);
              }}
              onRestore={() => returnVersion()}
              tabs={
                agentMode
                  ? [
                      { value: 'agent', label: 'Agente IA', icon: LuSparkles },
                      { value: 'events', label: `Eventos del WebHook · ${nodes.length}` },
                    ]
                  : null
              }
              activeTab={agentView}
              onTab={(value) => {
                setSelectedNode(null);
                setAgentView(value);
              }}
              workspaceActions={
                showAgentWorkspace ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AgentReadiness
                      items={agentReadinessItems}
                      theme={
                        isDark === 'true'
                          ? { cardBg: '#111b21', border: '#2a3942', textPrimary: '#e9edef', textSecondary: '#9fb4b9' }
                          : { cardBg: '#FFFFFF', border: '#e3e9ec', textPrimary: '#2D2D2D', textSecondary: '#747474' }
                      }
                    />
                    <button
                      type="button"
                      aria-pressed={agentTesting}
                      className="su-btn ghost"
                      onClick={() => setAgentTesting((value) => !value)}
                    >
                      {agentTesting ? 'Cerrar prueba' : 'Probar agente'}
                    </button>
                    <button type="button" className="su-btn" onClick={() => saveAgentConfig(agentDraft)} disabled={agentSaving}>
                      {agentSaving ? 'Guardando…' : 'Guardar'}
                    </button>
                  </div>
                ) : null
              }
              onSearch={() => setSearchOpen(true)}
              onUndo={undo}
              onRedo={redo}
              canUndo={historyState.canUndo}
              canRedo={historyState.canRedo}
              onLayout={handleAutoLayout}
              layoutRunning={autoLayoutRunning}
              dotColor={monitorDotColor}
              menuItems={[
                { label: 'Agente IA', icon: LuSparkles, onClick: () => openAgent('settings') },
                { label: 'Limpiar estados', icon: LuRefreshCw, onClick: () => setShowRemoveStatusesModal(true) },
                { label: 'Historial', icon: LuHistory, onClick: handleShowLogs },
                {
                  label: 'Monitor',
                  icon: LuActivity,
                  dot:
                    listDisconnectedNodes.length +
                      listDisconnectedOptions.length +
                      listNodesWithErrorInFields.length +
                      (transitionsDuplicateCount || 0) +
                      (triggersDuplicateCount || 0) >
                    0
                      ? monitorDotColor || '#FF2242'
                      : null,
                  onClick: () => handleErrorMonitoring(false),
                },
                { label: 'Diagnóstico', icon: LuStethoscope, onClick: handleShowDiagnostics },
              ]}
            />
            <div
              className="reactflow-wrapper su-canvas-area"
              ref={reactFlowWrapper}
            >
              {showAgentWorkspace && agentDraft && (
                <AgentWorkspace
                  key={editFlow?._id}
                  config={agentDraft}
                  onChange={setAgentDraft}
                  selection={agentSelection}
                  setSelection={setAgentSelection}
                  onSave={() => saveAgentConfig(agentDraft)}
                  saving={agentSaving}
                  dirty={agentDirty}
                  validation={agentValidation}
                  flows={editFlow?.flows || []}
                  nodes={nodes}
                  listAreas={listAreas}
                  listAdvisers={listAdvisers}
                  listTags={listTags}
                  listStages={listStages}
                  listLists={listLists}
                  isDark={isDark}
                  onAddTool={openToolPicker}
                  testing={agentTesting}
                  onStopTest={() => setAgentTesting(false)}
                  botId={editFlow?._id}
                  api={agentApi}
                  onTurn={handleAgentTurn}
                  liveTools={agentLiveTools}
                />
              )}
              {!selectedLog && agentMode && showToolPicker && (
                <AgentToolPicker
                  key={toolPickerType || 'all'}
                  initialType={toolPickerType}
                  tools={agentDraft?.tools || []}
                  onPick={addAgentTool}
                  onClose={() => setShowToolPicker(false)}
                  isDark={isDark}
                />
              )}
              <NodeSearchPalette
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
                nodes={nodes}
                onPick={goToSearchResult}
                theme={nodeTheme}
              />

              {isolatedNodeId && (
                <div
                  style={{
                    position: 'absolute',
                    top: '14px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 880,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '7px 10px 7px 13px',
                    borderRadius: '999px',
                    border: `1px solid ${nodeTheme.border}`,
                    backgroundColor: nodeTheme.panelBg,
                    boxShadow: '0 6px 18px rgba(0,0,0,0.22)',
                    fontSize: '12.5px',
                    color: nodeTheme.textPrimary,
                  }}
                >
                  <FiFilter style={{ color: nodeTheme.accent, fontSize: '14px' }} />
                  <span>
                    Rama aislada ·{' '}
                    <strong>
                      {nodes.find((n) => n.id === isolatedNodeId)?.data?.title ||
                        isolatedNodeId}
                    </strong>
                  </span>
                  <span style={{ color: nodeTheme.textTertiary }}>
                    {connectedNodeIds?.size ?? 0} de {nodes.length}
                  </span>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      borderLeft: `1px solid ${nodeTheme.border}`,
                      paddingLeft: '10px',
                      color: nodeTheme.textTertiary,
                    }}
                  >
                    <span>Alcance</span>
                    <button
                      type="button"
                      onClick={() =>
                        setFocusDepth((d) => Math.max(FOCUS_DEPTH_MIN, d - 1))
                      }
                      disabled={focusDepth <= FOCUS_DEPTH_MIN}
                      style={depthButtonStyle(nodeTheme, focusDepth <= FOCUS_DEPTH_MIN)}
                    >
                      −
                    </button>
                    <strong
                      style={{
                        color: nodeTheme.textPrimary,
                        minWidth: '14px',
                        textAlign: 'center',
                      }}
                    >
                      {focusDepth}
                    </strong>
                    <button
                      type="button"
                      onClick={() =>
                        setFocusDepth((d) => Math.min(FOCUS_DEPTH_MAX, d + 1))
                      }
                      disabled={focusDepth >= FOCUS_DEPTH_MAX || !focusHasMore}
                      title={
                        !focusHasMore ? 'Ya se ve la rama completa' : 'Ver un paso más'
                      }
                      style={depthButtonStyle(
                        nodeTheme,
                        focusDepth >= FOCUS_DEPTH_MAX || !focusHasMore
                      )}
                    >
                      +
                    </button>
                    {focusHasMore ? (
                      <span style={{ marginLeft: '2px' }}>· hay más</span>
                    ) : (
                      <span style={{ marginLeft: '2px' }}>· rama completa</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsolatedNodeId(null)}
                    style={{
                      border: `1px solid ${nodeTheme.border}`,
                      background: 'transparent',
                      color: nodeTheme.textSecondary,
                      borderRadius: '999px',
                      padding: '2px 9px',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    Mostrar todo · Esc
                  </button>
                </div>
              )}
              {autoLayoutRunning && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 900,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '18px',
                    backgroundColor:
                      isDark == 'true' ? 'rgba(11,17,24,0.72)' : 'rgba(248,250,252,0.78)',
                    backdropFilter: 'blur(3px)',
                    cursor: 'progress',
                    animation: 'autolayout-veil-in 0.18s ease-out',
                  }}
                  onPointerDownCapture={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onWheelCapture={(e) => e.stopPropagation()}
                >
                  <div className="autolayout-orbit">
                    <span />
                    <span />
                    <span />
                  </div>
                  <div
                    style={{
                      color: nodeTheme.textPrimary,
                      fontSize: '14px',
                      fontWeight: 600,
                      letterSpacing: '0.2px',
                    }}
                  >
                    Reordenando el flujo
                  </div>
                  <div style={{ color: nodeTheme.textSecondary, fontSize: '12px' }}>
                    Calculando rutas de {nodes.length} nodos…
                  </div>
                </div>
              )}
              <NodeThemeContext.Provider value={nodeTheme}>
              {/* Los Controls y el MiniMap de React Flow vienen con estilo claro
                  por defecto; los alineamos al tema del canvas. */}
              <style>{`
                .react-flow__controls {
                  display: flex;
                  flex-direction: row;
                  box-shadow: 0 4px 14px rgba(0,0,0,0.18);
                  border-radius: 10px;
                  overflow: hidden;
                  border: 1px solid ${nodeTheme.border};
                  margin-bottom: 16px;
                  z-index: 200;
                }
                .react-flow__controls-button {
                  background: ${nodeTheme.panelBg};
                  border-bottom: none;
                  border-right: 1px solid ${nodeTheme.border};
                  color: ${nodeTheme.textSecondary};
                  width: 32px;
                  height: 32px;
                }
                .react-flow__controls-button:last-child {
                  border-right: none;
                }
                .react-flow__controls-button:hover:not(:disabled) {
                  background: ${nodeTheme.inputBg};
                }
                .react-flow__controls-button:disabled {
                  opacity: 0.5;
                  cursor: default;
                }
                .react-flow__controls-button svg {
                  fill: ${nodeTheme.textSecondary};
                  max-width: 15px;
                  max-height: 15px;
                }
                .react-flow__minimap {
                  border-radius: 10px;
                  overflow: hidden;
                  border: 1px solid ${nodeTheme.border};
                  background: ${nodeTheme.panelBg};
                }
                /* Handles: siempre por encima de la tarjeta (al seleccionar un
                   nodo, el scale de la tarjeta los tapaba). */
                .su-bot-flow .react-flow__handle {
                  width: 10px;
                  height: 10px;
                  border: 2px solid ${nodeTheme.cardBg};
                  z-index: 5;
                  transition: box-shadow 0.15s ease, opacity 0.15s ease;
                }
                /* Zona de clic más grande que el punto visible. */
                .su-bot-flow .react-flow__handle::after {
                  content: '';
                  position: absolute;
                  inset: -8px;
                  border-radius: 50%;
                }
                /* En reposo las entradas son discretas para no ensuciar el canvas. */
                .su-bot-flow .react-flow__handle.target { opacity: 0.55; }
                /* Al pasar sobre un nodo, o con el nodo seleccionado, sus handles se resaltan. */
                .su-bot-flow .react-flow__node:hover .react-flow__handle,
                .su-bot-flow .react-flow__node.selected .react-flow__handle {
                  opacity: 1;
                  box-shadow: 0 0 0 4px ${nodeTheme.accent}40;
                }
                .su-bot-flow .react-flow__handle:hover {
                  opacity: 1;
                  box-shadow: 0 0 0 6px ${nodeTheme.accent}66;
                }
                /* Arrastrando una conexión: todas las entradas se vuelven zonas
                   para soltar, y la que está bajo el cursor se marca fuerte. */
                .su-bot-flow.is-connecting .react-flow__handle.target {
                  opacity: 1;
                  animation: su-handle-drop 1.2s ease-in-out infinite;
                }
                .su-bot-flow .react-flow__handle.connectingto,
                .su-bot-flow .react-flow__handle.valid {
                  opacity: 1;
                  animation: none;
                  box-shadow: 0 0 0 8px ${nodeTheme.accent}99;
                }
                @keyframes su-handle-drop {
                  0%, 100% { box-shadow: 0 0 0 4px ${nodeTheme.accent}55; }
                  50% { box-shadow: 0 0 0 9px ${nodeTheme.accent}22; }
                }
                @keyframes autolayout-spin { to { transform: rotate(360deg); } }
                .autolayout-spinning { animation: autolayout-spin 0.9s linear infinite; }
                @keyframes autolayout-veil-in { from { opacity: 0; } to { opacity: 1; } }
                @keyframes autolayout-orbit-spin {
                  to { transform: rotate(360deg); }
                }
                @keyframes autolayout-node-pulse {
                  0%, 100% { transform: scale(1); opacity: 0.55; }
                  50%      { transform: scale(1.55); opacity: 1; }
                }
                .autolayout-orbit {
                  position: relative;
                  width: 54px;
                  height: 54px;
                  animation: autolayout-orbit-spin 1.6s linear infinite;
                }
                .autolayout-orbit span {
                  position: absolute;
                  width: 12px;
                  height: 12px;
                  border-radius: 3px;
                  background: ${nodeTheme.accent || '#00A884'};
                  animation: autolayout-node-pulse 1.2s ease-in-out infinite;
                }
                .autolayout-orbit span:nth-child(1) {
                  top: 0; left: 50%; margin-left: -6px;
                  animation-delay: 0s;
                }
                .autolayout-orbit span:nth-child(2) {
                  bottom: 4px; left: 2px;
                  animation-delay: 0.4s;
                }
                .autolayout-orbit span:nth-child(3) {
                  bottom: 4px; right: 2px;
                  animation-delay: 0.8s;
                }
              `}</style>
              <ReactFlow
                nodes={displayNodes}
                edges={displayEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onConnectStart={onConnectStart}
                onConnectEnd={onConnectEnd}
                className={`su-bot-flow${isConnecting ? ' is-connecting' : ''}`}
                onReconnect={onReconnect}
                onReconnectStart={onReconnectStart}
                onReconnectEnd={onReconnectEnd}
                onDrop={onDrop}
                onDragOver={onDragOver}
                nodeTypes={memoizedNodeTypes}
                edgeTypes={memoizedEdgeTypes}
                onNodeDragStart={onNodeDragStart}
                onNodeDrag={onNodeDrag}
                onNodeDragStop={onNodeDragStop}
                onNodeClick={handleNodeClick}
                onNodeContextMenu={handleNodeContextMenu}
                onNodeMouseEnter={handleNodeMouseEnter}
                onNodeMouseLeave={handleNodeMouseLeave}
                minZoom={0.2}
                maxZoom={2}
                onInit={handleFlowInit}
                onlyRenderVisibleElements
              >
                <Background
                  color={nodeTheme.canvasDots}
                  variant={BackgroundVariant.Background}
                />
                <Controls position="bottom-center" showInteractive={false} />
                <MiniMap
                  nodeStrokeWidth={3}
                  pannable
                  zoomable
                  nodeColor={nodeColor}
                />
                <div className="d-flex justify-content-between">
                  <NodePalette
                    nodesDrawerWidth={nodesDrawerWidth}
                    setNodesDrawerWidth={setNodesDrawerWidth}
                    selectedLog={selectedLog}
                    showNodes={showNodes}
                    setShowNodes={setShowNodes}
                    orphanNodes={agentOrphanNodes}
                    onGoToNode={(id) => {
                      const target = nodes.find((n) => n.id === id);
                      if (target) goToNode(target);
                    }}
                  />

                  <NodeEditorPanel
                    selectedNode={selectedNode}
                    setSelectedNode={setSelectedNode}
                    setNodes={setNodes}
                    removeNode={removeNode}
                    nodes={nodes}
                    edges={edges}
                    editFlow={editFlow}
                    updateBot={updateBot}
                    truncateText={truncateText}
                    customStyles={customStyles}
                    customStylesDark={customStylesDark}
                    isDark={isDark}
                    watchSelectedNode={watchSelectedNode}
                    setLoading={setLoading}
                    valueSelectedVariable={valueSelectedVariable}
                    typeInputValue={typeInputValue}
                    typeInput={typeInput}
                    resetVariable={resetVariable}
                    contextVariables={contextVariables}
                    contextResponseApi={contextResponseApi}
                    handleMouseDown={handleMouseDown}
                    isDragging={isDragging}
                    isNodeExpanded={isNodeExpanded}
                    setIsNodeExpanded={setIsNodeExpanded}
                    position={position}
                    setPosition={setPosition}
                    selectedLog={selectedLog}
                    listAreas={listAreas}
                    listAdvisers={listAdvisers}
                    listTags={listTags}
                    listAssistants={listAssistants}
                    correspondence={correspondence}
                    flows={flows}
                    setConnectionTestData={setConnectionTestData}
                    setConnectionTestDataParams={setConnectionTestDataParams}
                    setModalConnectionTest={setModalConnectionTest}
                    setShowConditionsModal={setShowConditionsModal}
                    setAnchorElConditionsModel={setAnchorElConditionsModel}
                    makeStartNode={makeStartNode}
                  />

                  {errorMonitoring && (
                    <ErrorMonitor
                      handleErrorMonitoring={handleErrorMonitoring}
                      goToInitialNode={goToInitialNode}
                      goToNode={goToNode}
                      findNodeColor={findNodeColor}
                      listDisconnectedNodes={listDisconnectedNodes}
                      listDisconnectedOptions={listDisconnectedOptions}
                      listNodesWithErrorInFields={listNodesWithErrorInFields}
                      triggersDuplicateCount={triggersDuplicateCount}
                      transitionsDuplicateCount={transitionsDuplicateCount}
                      animateClass={animateClass}
                      isDark={isDark}
                    />
                  )}

                  {showPreview && (
                    <Preview
                      handlePreview={handlePreview}
                      chatBot={editFlow}
                      animateClass={animateClass}
                      isDark={isDark}
                      countries={Countries}
                      customStyles={customStyles}
                      customStylesDark={customStylesDark}
                    />
                  )}

                  {showLogs && (
                    <HistoryVersions
                      selectLog={selectLog}
                      handleShowLogs={handleShowLogs}
                      listLogs={listLogs}
                      animateClass={animateClass}
                      isDark={isDark}
                    />
                  )}

                  {showDiagnostics && (
                    <DiagnosticsPanel
                      rules={editFlow.diagnosticRules || []}
                      onChangeRules={handleChangeDiagnosticRules}
                      nodes={nodes}
                      tokens={{ botId: editFlow._id, companyId: editFlow.company }}
                      goToNode={goToNode}
                      findNodeColor={findNodeColor}
                      handleClose={handleShowDiagnostics}
                      animateClass={animateClass}
                      isDark={isDark}
                    />
                  )}
                </div>
              </ReactFlow>
              </NodeThemeContext.Provider>
            </div>
        </CanvasShell>
      )}

      {/* MODAL CONTEXT MENU */}
      {nodeContextModal && (
        <div
          style={{
            position: 'fixed',
            left: contextMenuPosition.x,
            top: contextMenuPosition.y,
            backgroundColor: isDark === 'true' ? '#1f2937' : '#ffffff',
            border: isDark === 'true' ? '1px solid #374151' : '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: isDark === 'true' 
              ? '0 10px 25px rgba(0, 0, 0, 0.5), 0 4px 6px rgba(0, 0, 0, 0.3)'
              : '0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 9999,
            minWidth: '160px',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => {
              if (!contextMenuNode) return;
              const newId = getId() + '-' + contextMenuNode.type;
              const newPosition = {
                x: contextMenuNode.position.x + 50,
                y: contextMenuNode.position.y + 50
              };
              
              const duplicatedNode = {
                ...contextMenuNode,
                id: newId,
                position: newPosition,
                data: {
                  ...JSON.parse(JSON.stringify(contextMenuNode.data)),
                  saved: true,
                  options: contextMenuNode.data.options ? contextMenuNode.data.options.map(opt => ({
                    ...opt,
                    id: newId + '-' + opt.id.split('-').slice(-2).join('-')
                  })) : []
                }
              };
              
              setNodes((nds) => [...nds, duplicatedNode]);
              const newNodes = [...nodes, duplicatedNode];
              
              setTimeout(() => {
                addNewNode(editFlow, newNodes, duplicatedNode);
              }, 500);
              
              setNodeContextModal(false);
            }}
            style={{
              width: '100%',
              padding: '10px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              color: isDark === 'true' ? '#f3f4f6' : '#374151',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDark === 'true' ? '#374151' : '#f3f4f6';
              e.target.style.color = isDark === 'true' ? '#ffffff' : '#1f2937';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = isDark === 'true' ? '#f3f4f6' : '#374151';
            }}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
            </svg>
            <span>Duplicar nodo</span>
          </button>
          <button
            onClick={() => {
              if (contextMenuNode) {
                setIsolatedNodeId((current) => {
                  if (current === contextMenuNode.id) return null;
                  setFocusDepth(FOCUS_DOWNSTREAM_DEPTH);
                  return contextMenuNode.id;
                });
              }
              setNodeContextModal(false);
            }}
            style={{
              width: '100%',
              padding: '10px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              color: isDark === 'true' ? '#f3f4f6' : '#374151',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                isDark === 'true' ? '#374151' : '#f3f4f6';
              e.currentTarget.style.color = isDark === 'true' ? '#ffffff' : '#1f2937';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = isDark === 'true' ? '#f3f4f6' : '#374151';
            }}
          >
            <FiFilter style={{ fontSize: '16px', flexShrink: 0 }} />
            <span>
              {isolatedNodeId === contextMenuNode?.id
                ? 'Mostrar todo el flujo'
                : 'Aislar esta rama'}
            </span>
          </button>
          <button
            onClick={async () => {
              if (contextMenuNode && (await copyNodeId(contextMenuNode.id))) {
                sendToast({ type: 'success', message: `ID copiado: ${contextMenuNode.id}` });
              }
              setNodeContextModal(false);
            }}
            style={{
              width: '100%',
              padding: '10px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              color: isDark === 'true' ? '#f3f4f6' : '#374151',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                isDark === 'true' ? '#374151' : '#f3f4f6';
              e.currentTarget.style.color = isDark === 'true' ? '#ffffff' : '#1f2937';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = isDark === 'true' ? '#f3f4f6' : '#374151';
            }}
          >
            <FiHash style={{ fontSize: '16px', flexShrink: 0 }} />
            <span>Copiar ID</span>
            <span
              style={{
                marginLeft: 'auto',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontSize: '11px',
                fontWeight: 400,
                opacity: 0.6,
              }}
            >
              #{shortNodeId(contextMenuNode?.id)}
            </span>
          </button>
          <button
            onClick={() => {
              if (contextMenuNode) {
                removeNode(contextMenuNode.id);
              }
              setNodeContextModal(false);
            }}
            style={{
              width: '100%',
              padding: '10px 16px',
              border: 'none',
              backgroundColor: 'transparent',
              color: isDark === 'true' ? '#f87171' : '#dc2626',
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDark === 'true' ? '#7f1d1d' : '#fef2f2';
              e.target.style.color = isDark === 'true' ? '#ffffff' : '#991b1b';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = isDark === 'true' ? '#f87171' : '#dc2626';
            }}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <polyline points="3,6 5,6 21,6"/>
              <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
              <line x1="10" y1="11" x2="10" y2="17"/>
              <line x1="14" y1="11" x2="14" y2="17"/>
            </svg>
            <span>Eliminar nodo</span>
          </button>
        </div>
      )}

      {/* MODAL REQUEST RESPONSE API */}
      <NodeModal
        open={modalConnectionTest}
        onClose={() => setModalConnectionTest(false)}
        width={550}
        isDark={isDark}
        icon={BiTransfer}
        color="#7ECCD8"
        eyebrow="Consulta API"
        title="Prueba de conexión"
      >
        <div
          id="content-list-chats"
          className="justify-content-center"
          style={{
            borderRadius: '4px',
          }}
        >
          <div
            className="p-2"
            style={{
              backgroundColor: 'rgba(126, 204, 216, 0.2)',
              borderRadius: '4px',
              fontSize: 11,
              color: '#7c7b7b',
            }}
          >
            <span style={{ fontWeight: 600 }}>Importante: </span>
            Esta prueba funcionará con los datos ya previamente configurados
            como URL de la api y Protocol HTTP. Por otro lado, los parametros
            como cabecera, parametros de petición entre otros, deben
            configurarse en la prueba directamente.
          </div>
          <div
            className="mt-4 py-2 px-3"
            style={{
              border: '1px solid #545454',
              borderRadius: '4px',
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div>Cabeceras (Opcional)</div>
              <div>
                <button
                  type="button"
                  style={{
                    border: 0,
                    borderRadius: '4px',
                    color: '#7ECCD8',
                    backgroundColor: 'rgba(126, 204, 216, 0.3)',
                    cursor: 'pointer',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    width: '150px',
                    zIndex: 100,
                  }}
                  onClick={() => {
                    const newHeader = {
                      key: '',
                      value: '',
                    };
                    setConnectionTestData((prev) => ({
                      ...prev,
                      headers: [...(prev.headers || []), newHeader],
                    }));
                  }}
                >
                  <IoMdAdd />
                  Agregar Cabecera
                </button>
              </div>
            </div>
            {connectionTestData?.headers?.length > 0 &&
              connectionTestData.headers.map((res, index) => (
                <div className="d-flex justify-content-around mt-2" key={index}>
                  <div className="d-flex align-items-center">
                    <div className="mr-2">
                      <input
                        className="form-control"
                        type="text"
                        style={{
                          width: '100%',
                          height: '35px',
                        }}
                        value={res.key || ''}
                        onChange={(e) => {
                          const newHeaders = [...connectionTestData.headers];
                          newHeaders[index].key = e.target.value;
                          setConnectionTestData((prev) => ({
                            ...prev,
                            headers: newHeaders,
                          }));
                        }}
                      />
                    </div>
                    <div className="ml-2">
                      <input
                        className="form-control"
                        type="text"
                        style={{
                          width: '100%',
                          height: '35px',
                        }}
                        value={res.value || ''}
                        onChange={(e) => {
                          const newHeaders = [...connectionTestData.headers];
                          newHeaders[index].value = e.target.value;
                          setConnectionTestData((prev) => ({
                            ...prev,
                            headers: newHeaders,
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      className="text-danger border-0 bg-transparent d-block pointer mt-2"
                      onClick={() => {
                        setConnectionTestData((prev) => ({
                          ...prev,
                          headers: prev.headers.filter((_, i) => i !== index),
                        }));
                      }}
                    >
                      <BsTrash3 style={{ fontSize: '16px' }} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div
            className="mt-4 py-2 px-3"
            style={{
              border: '1px solid #545454',
              borderRadius: '4px',
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div>Parametros (Opcional)</div>
              <div>
                <button
                  type="button"
                  style={{
                    border: 0,
                    borderRadius: '4px',
                    color: '#7ECCD8',
                    backgroundColor: 'rgba(126, 204, 216, 0.3)',
                    cursor: 'pointer',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    width: '150px',
                    zIndex: 100,
                  }}
                  onClick={() => {
                    const newParam = { key: '', value: '' };
                    setConnectionTestData((prev) => ({
                      ...prev,
                      params: [...(prev.params || []), newParam],
                    }));
                  }}
                >
                  <IoMdAdd />
                  Agregar Parametro
                </button>
              </div>
            </div>
            {connectionTestData?.params?.length > 0 &&
              connectionTestData.params.map((res, index) => (
                <div className="d-flex justify-content-around mt-2" key={index}>
                  <div className="d-flex align-items-center">
                    <div className="mr-2">
                      <input
                        className="form-control"
                        type="text"
                        style={{
                          width: '100%',
                          height: '35px',
                        }}
                        value={res.key || ''}
                        onChange={(e) => {
                          const newParams = [...connectionTestData.params];
                          newParams[index].key = e.target.value;
                          setConnectionTestData((prev) => ({
                            ...prev,
                            params: newParams,
                          }));
                        }}
                      />
                    </div>
                    <div className="ml-2">
                      <input
                        className="form-control"
                        type="text"
                        style={{
                          width: '100%',
                          height: '35px',
                        }}
                        value={res.value || ''}
                        onChange={(e) => {
                          const newParams = [...connectionTestData.params];
                          newParams[index].value = e.target.value;
                          setConnectionTestData((prev) => ({
                            ...prev,
                            params: newParams,
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      className="text-danger border-0 bg-transparent d-block pointer mt-2"
                      onClick={() => {
                        setConnectionTestData((prev) => ({
                          ...prev,
                          params: prev.params.filter((_, i) => i !== index),
                        }));
                      }}
                    >
                      <BsTrash3 style={{ fontSize: '16px' }} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div
            className="mt-4 py-2 px-3"
            style={{
              border: '1px solid #545454',
              borderRadius: '4px',
            }}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div>Cuerpo (Opcional)</div>
              <div>
                <button
                  type="button"
                  style={{
                    border: 0,
                    borderRadius: '4px',
                    color: '#7ECCD8',
                    backgroundColor: 'rgba(126, 204, 216, 0.3)',
                    cursor: 'pointer',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    width: '150px',
                    zIndex: 100,
                  }}
                  onClick={() => {
                    const newData = { key: '', value: '' };
                    setConnectionTestData((prev) => ({
                      ...prev,
                      data: [...(prev.data || []), newData],
                    }));
                  }}
                >
                  <IoMdAdd />
                  Agregar Dato
                </button>
              </div>
            </div>
            {connectionTestData?.data?.length > 0 &&
              connectionTestData.data.map((res, index) => (
                <div className="d-flex justify-content-around mt-2" key={index}>
                  <div className="d-flex align-items-center">
                    <div className="mr-2">
                      <input
                        className="form-control"
                        type="text"
                        style={{
                          width: '100%',
                          height: '35px',
                        }}
                        value={res.key || ''}
                        onChange={(e) => {
                          const newData = [...connectionTestData.data];
                          newData[index].key = e.target.value;
                          setConnectionTestData((prev) => ({
                            ...prev,
                            data: newData,
                          }));
                        }}
                      />
                    </div>
                    <div className="ml-2">
                      <input
                        className="form-control"
                        type="text"
                        style={{
                          width: '100%',
                          height: '35px',
                        }}
                        value={res.value || ''}
                        onChange={(e) => {
                          const newData = [...connectionTestData.data];
                          newData[index].value = e.target.value;
                          setConnectionTestData((prev) => ({
                            ...prev,
                            data: newData,
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end">
                    <button
                      className="text-danger border-0 bg-transparent d-block pointer mt-2"
                      onClick={() => {
                        setConnectionTestData((prev) => ({
                          ...prev,
                          data: prev.data.filter((_, i) => i !== index),
                        }));
                      }}
                    >
                      <BsTrash3 style={{ fontSize: '16px' }} />
                    </button>
                  </div>
                </div>
              ))}
          </div>
          <div className="mt-4 d-flex align-items-center">
            <button
              type="button"
              style={{
                border: 0,
                borderRadius: '4px',
                color: '#fff',
                backgroundColor: '#7ECCD8',
                cursor: 'pointer',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '12px',
                fontWeight: 'bold',
                width: '80px',
                height: '30px',
                zIndex: 100,
              }}
              onClick={() => requestApiNode()}
            >
              <div className="d-flex align-items-center">
                <BiTransfer className="mr-1" style={{ fontSize: '15px' }} />
                <span>Probar</span>
              </div>
            </button>
            {Object.keys(requestResponse).length > 0 && (
              <button
                type="button"
                className="ml-2"
                style={{
                  border: 0,
                  borderRadius: '4px',
                  color: '#fff',
                  backgroundColor: '#7ECCD8',
                  cursor: 'pointer',
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  width: '150px',
                  height: '30px',
                  zIndex: 100,
                }}
                onClick={(event) => {
                  responseToNode(event);
                }}
              >
                <span>Respuesta a Nodo</span>
              </button>
            )}
          </div>
          <div className="scrollable-content">
            <AceEditor
              mode="json"
              theme={isDark == 'true' ? 'github_dark' : 'github'}
              value={JSON.stringify(requestResponse, null, 2)}
              readOnly
              editorProps={{ $blockScrolling: true }}
              setOptions={{ useWorker: false }}
              style={{ width: '100%', height: '200px' }}
            />
          </div>
        </div>
      </NodeModal>

      {/* MODAL VARIABLES */}
      <NodeModal
        open={variablesModal}
        onClose={() => setVariablesModal(false)}
        anchorEl={anchorEl}
        width={360}
        isDark={isDark}
        icon={VscVariableGroup}
        color="#5BC0DE"
        eyebrow="Insertar"
        title="Variables disponibles"
      >
        <div>
          <div className="su-control-group pb-2">
            <Select
              styles={
                isDark == 'true'
                  ? {
                      ...customStylesDark,
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 1300,
                      }),
                    }
                  : {
                      ...customStyles,
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 1300,
                      }),
                    }
              }
              className="form-control basic-multi-select"
              placeholder="Seleccione..."
              name="variables"
              onChange={(val) => {
                if (typeInput == 'node') {
                  setValueSelectedVariable(val.fieldCorresp);
                } else {
                  setSelectedNode((prevNode) => {
                    let conditions = [...prevNode.data.conditions];
                    const keys = typeInputValue
                      .replace(/\[|\]/g, '')
                      .split('.');
                    let obj = conditions;
                    for (let i = 0; i < keys.length - 1; i++) {
                      const key = keys[i];
                      if (!obj[key]) {
                        obj[key] = {};
                      }
                      obj = obj[key];
                    }
                    obj[keys[keys.length - 1]] = val.fieldCorresp;

                    return {
                      ...prevNode,
                      data: {
                        ...prevNode.data,
                        conditions: conditions,
                      },
                    };
                  });
                }
              }}
              options={
                typeInput === 'nodeCondition'
                  ? [
                      {
                        fieldName: 'Respuesta nodo',
                        fieldCorresp: '{{response_node}}',
                      },
                      {
                        fieldName: 'Respuesta API',
                        fieldCorresp: '{{response_api}}',
                      },
                      ...correspondence,
                    ]
                  : correspondence
              }
              classNamePrefix="select"
              noOptionsMessage={() => 'Sin opciones'}
              getOptionLabel={(option) => (
                <div className="option d-flex" style={{ fontSize: '15px' }}>
                  {option.fieldName}
                </div>
              )}
              getOptionValue={(option) => option.fieldName}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
              menuShouldScrollIntoView={false}
            />
          </div>
          {contextVariables && contextVariables.length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div
                style={{
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: nodeTheme.textTertiary,
                  marginBottom: 8,
                }}
              >
                Variables de API
              </div>
              {contextVariables.map((context, idx) => (
                <div key={context.id || idx} style={{ marginBottom: 12 }}>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      marginBottom: 4,
                      color: nodeTheme.textSecondary,
                    }}
                  >
                    {context.api || context.label || `API ${idx + 1}`}
                  </div>
                  <Select
                    styles={
                      isDark == 'true'
                        ? {
                            ...customStylesDark,
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 1300,
                            }),
                          }
                        : {
                            ...customStyles,
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 1300,
                            }),
                          }
                    }
                    className="form-control basic-multi-select"
                    placeholder={`Buscar variable de ${
                      context.api || context.label || `API ${idx + 1}`
                    }...`}
                    name={`variablesApi_${context.id || idx}`}
                    options={context.data}
                    classNamePrefix="select"
                    noOptionsMessage={() => 'Sin opciones'}
                    menuPortalTarget={document.body}
                    menuPosition="absolute"
                    menuPlacement="auto"
                    menuShouldScrollIntoView={true}
                    maxMenuHeight={350}
                    isSearchable={true}
                    components={{
                      MenuList: (props) => (
                        <div
                          style={{
                            height: '100%',
                            maxHeight: 350,
                            minWidth: 260,
                            overflowY: 'auto',
                            background: 'transparent',
                            padding: 0,
                            borderRadius: 0,
                          }}
                        >
                          <VariableTree
                            data={context.data}
                            onSelect={(val) => {
                              if (typeInput == 'node') {
                                setValueSelectedVariable(val.value);
                              } else {
                                setSelectedNode((prevNode) => {
                                  let conditions = [
                                    ...prevNode.data.conditions,
                                  ];
                                  const keys = typeInputValue
                                    .replace(/\[|\]/g, '')
                                    .split('.');
                                  let obj = conditions;
                                  for (let i = 0; i < keys.length - 1; i++) {
                                    const key = keys[i];
                                    if (!obj[key]) {
                                      obj[key] = {};
                                    }
                                    obj = obj[key];
                                  }
                                  obj[keys[keys.length - 1]] = val;

                                  return {
                                    ...prevNode,
                                    data: {
                                      ...prevNode.data,
                                      conditions: conditions,
                                    },
                                  };
                                });
                              }
                            }}
                            isDark={isDark}
                          />
                        </div>
                      ),
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </NodeModal>

      {/* MODAL EDIT NAME */}
      <NodeModal
        open={showEditName}
        onClose={() => {
          setShowEditName(false);
          setEditName('');
        }}
        width={400}
        isDark={isDark}
        icon={BsPencilFill}
        color="#5BC0DE"
        eyebrow="Bot"
        title="Editar nombre"
      >
        <div>
          <input
            className="form-control"
            type="text"
            name="name"
            id="name"
            value={editName}
            onChange={(e) => {
              setEditName(e.target.value);
            }}
          />

          <div
            className="d-flex justify-content-end mt-4"
            style={{ gap: '8px' }}
          >
            <NodeModalButton
              isDark={isDark}
              onClick={() => {
                setShowEditName(false);
                setEditName('');
              }}
            >
              Cancelar
            </NodeModalButton>
            <NodeModalButton
              variant="primary"
              color="#5BC0DE"
              isDark={isDark}
              disabled={!editName.trim()}
              onClick={() => updateNameBot(editFlow)}
            >
              Guardar
            </NodeModalButton>
          </div>
        </div>
      </NodeModal>

      {/* MODAL RESPONSE TO NODE */}
      <NodeModal
        open={responseToNodeModal}
        onClose={() => {
          setResponseToNodeModal(false);
          setListToNodeData({
            description: null,
            options: [],
          });
        }}
        anchorEl={anchorEl2}
        width={400}
        isDark={isDark}
        icon={IoMdAdd}
        color="#00A884"
        eyebrow="Respuesta básica"
        title="Agregar botones u opciones"
      >
        <div
          style={{
            overflowX: 'hidden',
          }}
        >
          <div>
            <textarea
              className="form-control"
              placeholder="Escribe la respuesta del Bot"
              style={{
                width: '100%',
                height: '80px',
                borderRadius: '4px',
              }}
              value={listToNodeData.description || ''}
              onChange={(e) => {
                setListToNodeData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }));
              }}
            />
          </div>
          <div>
            <button
              type="button"
              style={{
                border: 0,
                borderRadius: '4px',
                color: '#7ECCD8',
                backgroundColor: 'rgba(126, 204, 216, 0.3)',
                cursor: 'pointer',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '12px',
                fontWeight: 'bold',
                width: '100%',
                zIndex: 100,
              }}
              onClick={() => {
                const length = listToNodeData.options.length;
                const newOption = {
                  label: '',
                  id:
                    selectedNode.id +
                    '-' +
                    (length + 1) +
                    '-Opción-' +
                    (length + 1),
                  name: 'titulo',
                };
                setListToNodeData((prev) => ({
                  ...prev,
                  options: [...(prev.options || []), newOption],
                }));
              }}
              disabled={listToNodeData.options.length >= 10}
            >
              <IoMdAdd />
              Agregar Opción
            </button>
          </div>
          {listToNodeData?.options?.length > 0 &&
            listToNodeData.options.map((opt, index) => {
              const maxLengthValue =
                listToNodeData?.options?.length > 3 ? 24 : 20;
              return (
                <div className="d-flex justify-content-around mt-2" key={index}>
                  <div style={{ width: '80%', height: '35px' }}>
                    <input
                      className="form-control"
                      type="text"
                      value={opt.label || ''}
                      onChange={(e) => {
                        const newOption = [...listToNodeData.options];
                        newOption[index].label = e.target.value;
                        setListToNodeData((prev) => ({
                          ...prev,
                          options: newOption,
                        }));
                      }}
                      maxLength={maxLengthValue}
                    />
                  </div>
                  <div className="ml-2 d-flex align-intems-center">
                    <button
                      className="border-0 bg-transparent d-block pointer mt-2"
                      onClick={(event) => {
                        setVariablesOptionsModal(true);
                        setAnchorEl(event.currentTarget);
                        setIndexOptionResToNode(index);
                      }}
                    >
                      <LuListTree
                        style={{
                          fontSize: '16px',
                          color: '#61676B',
                        }}
                      />
                    </button>
                    <button
                      className="text-danger border-0 bg-transparent d-block pointer mt-2"
                      onClick={() => {
                        setListToNodeData((prev) => ({
                          ...prev,
                          options: prev.options.filter((_, i) => i !== index),
                        }));
                      }}
                    >
                      <BsTrash3 style={{ fontSize: '16px' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          <div className="mt-2">
            <button
              type="button"
              style={{
                backgroundColor: '#7ECCD8',
                border: 0,
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: '12px',
                fontWeight: 'bold',
                height: '30px',
                width: '100%',
              }}
              className="mr-2"
              onClick={convertToNode}
            >
              <span>Convertir</span>
            </button>
          </div>
        </div>
      </NodeModal>

      {/* MODAL VARIABLES OPTIONS RESPONSE TO NODE*/}
      <NodeModal
        open={variablesOptionsModal}
        onClose={() => setVariablesOptionsModal(false)}
        anchorEl={anchorEl}
        width={320}
        isDark={isDark}
        icon={VscVariableGroup}
        color="#5BC0DE"
        eyebrow="Insertar"
        title="Respuesta del nodo"
      >
        <RichTreeView
          sx={{ color: nodeTheme.textSecondary }}
          items={listOptionsResToNode}
          onSelectedItemsChange={(event, val) => {
            if (val !== 'response') {
              const maxLengthValue =
                listToNodeData?.options?.length > 3 ? 24 : 20;
              const selectedNode = findNodeById(listOptionsResToNode, val);

              if (selectedNode) {
                const selectedLabel = selectedNode.label;
                let selectedValue = selectedLabel.substring(
                  selectedLabel.indexOf(':') + 2
                );

                if (selectedValue.length > maxLengthValue) {
                  selectedValue = selectedValue.substring(0, maxLengthValue);
                }

                const newOption = [...listToNodeData.options];
                newOption[indexOptionResToNode].label = selectedValue;
                setListToNodeData((prev) => ({
                  ...prev,
                  options: newOption,
                }));
              }
            }
          }}
        />
      </NodeModal>

      {/* MODAL CONDITIONS*/}
      <NodeModal
        open={showConditionsModal}
        onClose={() => setShowConditionsModal(false)}
        anchorEl={anchorElConditionsModel}
        width={480}
        isDark={isDark}
        icon={FaArrowRightArrowLeft}
        color="#E5253D"
        eyebrow="Condiciones"
        title="Configurar condición"
      >
        <form onSubmit={handleSubmit(saveConditions)} style={{ width: '100%' }}>
          <div>
            <div className="pt-2">
              {selectedNode?.data?.conditions?.map((condition, index) => (
                <div key={condition.id || index}>
                  {index > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        margin: '10px 0',
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: '1px',
                          backgroundColor: nodeTheme.border,
                        }}
                      />
                      <div
                        style={{
                          padding: '3px 10px',
                          borderRadius: '6px',
                          border: `1px solid ${nodeTheme.border}`,
                          backgroundColor: nodeTheme.panelBg,
                          color: nodeTheme.textSecondary,
                          fontWeight: 700,
                          fontSize: '10px',
                          letterSpacing: '0.06em',
                        }}
                      >
                        {condition.type || 'AND'}
                      </div>
                      <div
                        style={{
                          flex: 1,
                          height: '1px',
                          backgroundColor: nodeTheme.border,
                        }}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      padding: '12px',
                      borderRadius: '10px',
                      border: `1px solid ${nodeTheme.border}`,
                      backgroundColor: nodeTheme.inputBg,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '10px',
                      }}
                    >
                      <span
                        style={{
                          padding: '2px 7px',
                          borderRadius: '5px',
                          backgroundColor: '#E5253D1F',
                          color: '#E5253D',
                          fontSize: '9px',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                        }}
                      >
                        REGLA {index + 1}
                      </span>
                      <div style={{ flex: 1 }} />
                      <button
                        type="button"
                        title="Eliminar regla"
                        className="border-0 bg-transparent d-block pointer"
                        onClick={() => removeCondition(index)}
                      >
                        <BsTrash3
                          style={{
                            fontSize: '14px',
                            color: nodeTheme.textTertiary,
                          }}
                        />
                      </button>
                    </div>
                    <div>
                      {!['status', 'whatsapp_message', 'window_24h'].includes(condition.rule.operatorType) && (
                        <div className="pb-2">
                          <SUTextField
                            id={`conditions_${condition.id}_${selectedNode.id}_rule_value1`}
                            name={`conditions_${condition.id}_${selectedNode.id}_rule_value1`}
                            type="text"
                            showVariables
                            selectedNode={selectedNode}
                            contextVariables={contextVariables}
                            getValues={getValues}
                            setValue={setValue}
                            errors={errors}
                            {...register(
                              `conditions_${condition.id}_${selectedNode.id}_rule_value1`,
                              {
                                validate: (value) => {
                                  const operatorType = condition.rule?.operatorType;
                                  if (!['status', 'whatsapp_message', 'window_24h'].includes(operatorType)) {
                                    if (
                                      condition.rule?.value1 &&
                                      condition.rule.value1.trim() !== ''
                                    ) {
                                      return true;
                                    }
                                    if (!value || value.trim() === '') {
                                      return 'Este campo es requerido.';
                                    }
                                    if (!/\S/.test(value)) {
                                      return 'No puede ser solo espacios en blanco.';
                                    }
                                  }
                                  return true;
                                },
                                onChange: (e) => {
                                  setSelectedNode((node) => {
                                    const updatedConditions = [
                                      ...node.data.conditions,
                                    ];
                                    updatedConditions[index].rule.value1 =
                                      e.target.value;
                                    return {
                                      ...node,
                                      data: {
                                        ...node.data,
                                        conditions: updatedConditions,
                                      },
                                    };
                                  });
                                },
                              }
                            )}
                          />
                        </div>
                      )}
                      <div className="su-control-group pb-2">
                        <Select
                          styles={{
                            ...(isDark === 'true'
                              ? customStylesDark
                              : customStyles),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            menuList: (provided) => ({
                              ...provided,
                              scrollbarWidth: 'thin',
                            }),
                          }}
                          classNamePrefix="select"
                          className="form-control basic-multi-select"
                          placeholder="Seleccionar operador"
                          options={listOptionsOperators}
                          name={`conditions[${index}].rule.operator`}
                          onChange={(value) => {
                            setSelectedNode((node) => {
                              node.data.conditions[index].rule.operator = value;
                              node.data.conditions[index].rule.operatorType =
                                value.type;
                              node.data.conditions[
                                index
                              ].rule.operatorComparable = value.type;

                              return {
                                ...node,
                                data: {
                                  ...node.data,
                                  conditions: node.data.conditions,
                                },
                              };
                            });
                          }}
                          value={condition.rule.operator || null}
                          noOptionsMessage={() => 'Sin opciones'}
                          getOptionLabel={(option) => (
                            <div
                              className="option d-flex"
                              style={{
                                fontSize: '15px',
                              }}
                            >
                              {option.label}
                            </div>
                          )}
                          getOptionValue={(option) => option.id}
                          components={{
                            MenuList: (props) => (
                              <CustomMenuList {...props} isDark={isDark} />
                            ),
                          }}
                          isSearchable
                          menuPortalTarget={document.body}
                        />
                        {errors?.conditions?.[index]?.rule?.operator && (
                          <Alert>
                            * {errors.conditions[index].rule.operator.message}
                          </Alert>
                        )}
                      </div>
                      {![
                        'basic',
                        'status',
                        'format',
                        'array',
                        'object',
                        'http_error',
                        'whatsapp_message',
                        'window_24h',
                      ].includes(condition.rule.operatorType) && (
                        <div>
                          <SUTextField
                            id={`conditions_${condition.id}_${selectedNode.id}_rule_value2`}
                            name={`conditions_${condition.id}_${selectedNode.id}_rule_value2`}
                            type="text"
                            showVariables
                            selectedNode={selectedNode}
                            contextVariables={contextVariables}
                            getValues={getValues}
                            setValue={setValue}
                            errors={errors}
                            {...register(
                              `conditions_${condition.id}_${selectedNode.id}_rule_value2`,
                              {
                                validate: (value) => {
                                  const operatorType = condition.rule?.operatorType;
                                  const notRequiredTypes = [
                                    'basic',
                                    'status',
                                    'format',
                                    'array',
                                    'object',
                                    'http_error',
                                    'whatsapp_message',
                                    'window_24h',
                                  ];
                                  if (!notRequiredTypes.includes(operatorType)) {
                                    if (
                                      condition.rule?.value2 &&
                                      condition.rule.value2.trim() !== ''
                                    ) {
                                      return true;
                                    }
                                    if (!value || value.trim() === '') {
                                      return 'Este campo es requerido.';
                                    }
                                    if (!/\S/.test(value)) {
                                      return 'No puede ser solo espacios en blanco.';
                                    }
                                  }
                                  return true;
                                },
                                onChange: (e) => {
                                  setSelectedNode((node) => {
                                    const updatedConditions = [
                                      ...node.data.conditions,
                                    ];
                                    updatedConditions[index].rule.value2 =
                                      e.target.value;
                                    return {
                                      ...node,
                                      data: {
                                        ...node.data,
                                        conditions: updatedConditions,
                                      },
                                    };
                                  });
                                },
                              }
                            )}
                          />
                        </div>
                      )}
                      {['format'].includes(condition.rule.operatorType) &&
                        ['date', 'hour'].includes(
                          condition.rule.operator.id
                        ) && (
                          <div>
                            <div
                              style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                              }}
                            >
                              <Select
                                styles={{
                                  ...(isDark === 'true'
                                    ? customStylesDark
                                    : customStyles),
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 1300,
                                  }),
                                  menuList: (provided) => ({
                                    ...provided,
                                    scrollbarWidth: 'thin',
                                  }),
                                }}
                                className="form-control basic-multi-select"
                                placeholder="Seleccionar"
                                value={
                                  (condition.rule?.operator.id === 'date'
                                    ? dateFormatOptions
                                    : timeFormatOptions
                                  )?.find(
                                    (opt) => opt.id === condition.rule?.value2
                                  ) || null
                                }
                                name="formatConditions"
                                onChange={(value) => {
                                  setSelectedNode((node) => {
                                    node.data.conditions[index].rule.value2 =
                                      value.id;
                                    if (
                                      ![
                                        'current_time_offset',
                                        'current_time_max_offset',
                                        'current_time_tomorrow_max_offset',
                                      ].includes(value.id)
                                    ) {
                                      node.data.conditions[index].rule.value3 =
                                        null;
                                    }

                                    return {
                                      ...node,
                                      data: {
                                        ...node.data,
                                        conditions: node.data.conditions,
                                      },
                                    };
                                  });
                                }}
                                options={
                                  condition.rule.operator.id === 'date'
                                    ? dateFormatOptions
                                    : timeFormatOptions
                                }
                                classNamePrefix="select"
                                noOptionsMessage={() => 'Sin opciones'}
                                getOptionLabel={(option) => (
                                  <div
                                    className="option d-flex"
                                    style={{
                                      fontSize: '15px',
                                    }}
                                  >
                                    {option.label}
                                  </div>
                                )}
                                getOptionValue={(option) => option.id}
                              />
                            </div>
                          </div>
                        )}
                      {condition.rule?.operatorType === 'format' &&
                        condition.rule?.operator?.id === 'hour' &&
                        [
                          'current_time_offset',
                          'current_time_max_offset',
                          'current_time_tomorrow_max_offset',
                        ].includes(condition.rule?.value2) && (
                          <div className="pt-2">
                            <SUTextField
                              id={`conditions_${condition.id}_${selectedNode.id}_rule_value3`}
                              name={`conditions_${condition.id}_${selectedNode.id}_rule_value3`}
                              type="text"
                              placeholder="Solo minutos. Ej: 30"
                              selectedNode={selectedNode}
                              contextVariables={contextVariables}
                              getValues={getValues}
                              setValue={setValue}
                              errors={errors}
                              {...register(
                                `conditions_${condition.id}_${selectedNode.id}_rule_value3`,
                                {
                                  validate: (value) => {
                                    const shouldValidate =
                                      condition.rule?.operatorType ===
                                        'format' &&
                                      condition.rule?.operator?.id === 'hour' &&
                                      [
                                        'current_time_offset',
                                        'current_time_max_offset',
                                        'current_time_tomorrow_max_offset',
                                      ].includes(condition.rule?.value2);
                                    if (!shouldValidate) {
                                      return true;
                                    }
                                    if (!value || value.trim() === '') {
                                      return 'Este campo es requerido.';
                                    }
                                    const validPattern = /^\s*\d+\s*$/;
                                    if (!validPattern.test(value)) {
                                      return 'Formato invalido. Ingresa solo minutos (ej: 30).';
                                    }
                                    return true;
                                  },
                                  onChange: (e) => {
                                    setSelectedNode((node) => {
                                      const updatedConditions = [
                                        ...node.data.conditions,
                                      ];
                                      updatedConditions[index].rule.value3 =
                                        e.target.value;
                                      return {
                                        ...node,
                                        data: {
                                          ...node.data,
                                          conditions: updatedConditions,
                                        },
                                      };
                                    });
                                  },
                                }
                              )}
                            />
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ))}
              <div className="d-flex pt-3" style={{ gap: '8px' }}>
                {['and', 'or'].map((kind) => (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => addRule(kind)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      border: `1px solid ${nodeTheme.border}`,
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      color: nodeTheme.textSecondary,
                      fontSize: '11px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    + Regla {kind.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {conditionsSentence && (
            <div
              style={{
                marginTop: '14px',
                padding: '11px 13px',
                borderRadius: '8px',
                borderLeft: '3px solid #E5253D',
                backgroundColor: '#E5253D14',
              }}
            >
              <div
                style={{
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color: '#E5253D',
                  marginBottom: '4px',
                }}
              >
                EN PALABRAS
              </div>
              <div
                style={{
                  fontSize: '11px',
                  lineHeight: 1.5,
                  color: nodeTheme.textPrimary,
                }}
              >
                {conditionsSentence}
              </div>
            </div>
          )}

          <div style={{ marginTop: '14px' }}>
            <div
              style={{
                fontSize: '9px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                color: nodeTheme.textTertiary,
                marginBottom: '7px',
              }}
            >
              SALIDAS DEL NODO
            </div>
            {[
              ['#4ADE80', selectedNode?.data?.succes, 'Se cumplen todas las reglas'],
              ['#F87171', selectedNode?.data?.failed, 'Cualquier otro caso'],
            ].map(([dot, label, hint]) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '9px',
                  padding: '8px 10px',
                  marginBottom: '6px',
                  borderRadius: '8px',
                  border: `1px solid ${nodeTheme.border}`,
                  backgroundColor: nodeTheme.inputBg,
                }}
              >
                <span
                  style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    backgroundColor: dot,
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: nodeTheme.textPrimary,
                    }}
                  >
                    {label}
                  </div>
                  <div
                    style={{ fontSize: '10px', color: nodeTheme.textTertiary }}
                  >
                    {hint}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            className="d-flex justify-content-end align-items-center"
            style={{ gap: '8px', marginTop: '18px' }}
          >
            <NodeModalButton
              isDark={isDark}
              onClick={() => setShowConditionsModal(false)}
            >
              Cancelar
            </NodeModalButton>
            <NodeModalButton
              type="submit"
              variant="primary"
              color="#E5253D"
              isDark={isDark}
            >
              Guardar condición
            </NodeModalButton>
          </div>
        </form>
      </NodeModal>

      {/* MODAL PARA REMOVER ESTADOS */}
      <NodeModal
        open={showRemoveStatusesModal}
        onClose={() => setShowRemoveStatusesModal(false)}
        width={400}
        isDark={isDark}
        icon={MdCleaningServices}
        color="#E5253D"
        eyebrow="Mantenimiento"
        title="Limpiar estados"
      >
        <RemoveStatuses
          statusModal={setShowRemoveStatusesModal}
          editFlow={editFlow}
          isDark={isDark}
          countries={Countries}
          customStyles={customStyles}
          customStylesDark={customStylesDark}
        />
      </NodeModal>

      {/* MODAL CONFIRM TEMPLATE */}
      <NodeModal
        open={showModalCreateTemplate}
        onClose={() => setShowModalCreateTemplate(false)}
        width={400}
        isDark={isDark}
        icon={TbLayoutGrid}
        color="#5BC0DE"
        eyebrow="Plantillas"
        title="Confirmar plantilla"
      >
        <div>
          <div style={{ fontSize: '13px', lineHeight: 1.55 }}>
            ¿Estás seguro de crear el bot a partir de esta plantilla?
          </div>

          <div
            className="d-flex justify-content-end mt-4"
            style={{ gap: '8px' }}
          >
            <NodeModalButton
              isDark={isDark}
              onClick={() => {
                setNameBot('');
                setDescriptionBot('');
                setNodes([]);
                setEdges([]);
                setShowModalCreateTemplate(false);
              }}
            >
              Cancelar
            </NodeModalButton>
            <NodeModalButton
              variant="primary"
              color="#5BC0DE"
              isDark={isDark}
              onClick={() => {
                setShowModalCreateTemplate(false);
                setMode('create');
                createBot();
              }}
            >
              Crear bot
            </NodeModalButton>
          </div>
        </div>
      </NodeModal>

      {/* MODAL CREATE BOT */}
      <NodeModal
        open={modalCreate}
        onClose={() => setModalCreate(false)}
        width={550}
        isDark={isDark}
        icon={IoMdAdd}
        color="#00A884"
        eyebrow="Bots"
        title="Crear bot"
      >
        <form onSubmit={handleSubmit(createBot)} style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div>
              <label>Ingresa un nombre para el bot</label>
              <div>
                <input
                  className="form-control"
                  type="text"
                  name="name"
                  id="name"
                  {...register('nameBot', {
                    required: {
                      value: modalCreate,
                      message: 'El campo es requerido.',
                    },
                    pattern: {
                      value: /\S+/,
                      message: 'No puede ser solo espacios en blanco',
                    },
                  })}
                />
                {errors.nameBot && <Alert>* {errors.nameBot.message}</Alert>}
              </div>
              <label className="pt-4">
                Ingresa una descripción para el bot
              </label>
              <div>
                <input
                  className="form-control"
                  type="text"
                  name="description"
                  id="description"
                  {...register('descriptionBot', {
                    required: {
                      value: modalCreate,
                      message: 'El campo es requerido.',
                    },
                    pattern: {
                      value: /\S+/,
                      message: 'No puede ser solo espacios en blanco',
                    },
                  })}
                />
                {errors.descriptionBot && (
                  <Alert>* {errors.descriptionBot.message}</Alert>
                )}
              </div>
              <label className="pt-4">
                Selecciona el tipo de bot
              </label>
              <div>
                <Select
                  styles={isDark == 'true' ? customStylesDark : customStyles}
                  className="form-control basic-multi-select"
                  placeholder="Seleccione el tipo..."
                  defaultValue={botTypeOptions[0]}
                  {...register('botType', {
                    required: {
                      value: modalCreate,
                      message: 'El campo es requerido.',
                    },
                  })}
                  onChange={(selectedOption) => {
                    setValue('botType', selectedOption.value);
                  }}
                  options={botTypeOptions}
                  classNamePrefix="select"
                  noOptionsMessage={() => 'Sin opciones'}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.value}
                />
                {errors.botType && (
                  <Alert>* {errors.botType.message}</Alert>
                )}
              </div>
            </div>

            <div className="row justify-content-end bd col-12 mt-4">
              <a
                href="javascript:;"
                className="btn btn-lg btn-default mr-1"
                onClick={() => {
                  setModalCreate(false);
                  reset();
                }}
              >
                Cancelar
              </a>
              <input
                className="btn btn-lg btn-info"
                type="submit"
                value="Crear"
              />
            </div>
          </div>
        </form>
      </NodeModal>

      {/* MODAL CREATE BOT WITH AI */}
      <NodeModal
        open={modalBotAssistant}
        onClose={() => setModalBotAssistant(false)}
        width={650}
        isDark={isDark}
        icon={FaBrain}
        color="#A7ADA7"
        eyebrow="Bots"
        title="Crear bot con IA"
      >
        <Content>
          <form
            onSubmit={handleSubmit(createBotWithIA)}
            style={{ width: '100%' }}
          >
            <div className="scrollable-content scrollable-content-overflow">
              <div
                className="su-control su-with-prefix su-with-suffix su-segmentation-disable"
                style={{ marginTop: '0px' }}
              >
                <label>Nombre del Bot: </label>
                <div className="su-control-group">
                  <input
                    className="form-control px-2"
                    rows="4"
                    type="text"
                    name="name"
                    {...register('name_bot_ai', {
                      required: {
                        value: modalBotAssistant,
                        message: 'El campo es requerido.',
                      },
                      pattern: {
                        value: /\S+/,
                        message: 'No puede ser solo espacios en blanco',
                      },
                    })}
                  ></input>
                </div>
                {errors.name_bot_ai && (
                  <Alert>* {errors.name_bot_ai.message}</Alert>
                )}
              </div>
              <div className="su-control su-with-prefix su-with-suffix su-segmentation-disable">
                <label>Descripción del Bot: </label>
                <div className="su-control-group">
                  <input
                    className="form-control px-2"
                    rows="4"
                    type="text"
                    name="description"
                    {...register('description_bot_ai', {
                      required: {
                        value: modalBotAssistant,
                        message: 'El campo es requerido.',
                      },
                      pattern: {
                        value: /\S+/,
                        message: 'No puede ser solo espacios en blanco',
                      },
                    })}
                  ></input>
                </div>
                {errors.description_bot_ai && (
                  <Alert>* {errors.description_bot_ai.message}</Alert>
                )}
              </div>
              <div className="su-control su-with-prefix su-with-suffix su-segmentation-disable">
                <label>Tipo de Bot: </label>
                <div className="su-control-group">
                  <Select
                    styles={isDark == 'true' ? customStylesDark : customStyles}
                    className="form-control basic-multi-select"
                    placeholder="Seleccione el tipo..."
                    defaultValue={botTypeOptions[0]}
                    {...register('bot_type_ai', {
                      required: {
                        value: modalBotAssistant,
                        message: 'El campo es requerido.',
                      },
                    })}
                    onChange={(selectedOption) => {
                      setValue('bot_type_ai', selectedOption.value);
                    }}
                    options={botTypeOptions}
                    classNamePrefix="select"
                    noOptionsMessage={() => 'Sin opciones'}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                  />
                </div>
                {errors.bot_type_ai && (
                  <Alert>* {errors.bot_type_ai.message}</Alert>
                )}
              </div>
              <div className="su-control su-with-prefix su-with-suffix su-segmentation-disable">
                <label>Instrucciones para crear el Bot: </label>
                <label className="f-w-100 font-italic d-block">
                  Instrucciones para crear tu Bot con IA Cuéntanos brevemente de
                  qué trata tu negocio y qué necesitas que haga el bot. Con esta
                  información podremos crear un bot básico que se ajuste a tus
                  objetivos.
                  <br />
                  <br />
                  💬 Ejemplo: “Tengo una tienda de ropa online y quiero un bot
                  que ayude a responder preguntas sobre tallas, medios de pago y
                  tiempos de entrega.”
                  <br />
                  <br />
                </label>
                <div className="su-control-group">
                  <textarea
                    className="form-control px-2"
                    placeholder="Escribe las instrucciones..."
                    rows="4"
                    type="text"
                    name="content"
                    {...register('instructions_bot', {
                      required: {
                        value: modalBotAssistant,
                        message: 'El campo es requerido.',
                      },
                      maxLength: {
                        value: 1000,
                        message: 'El máximo es de 1000 caracteres.',
                      },
                      pattern: {
                        value: /\S+/,
                        message: 'No puede ser solo espacios en blanco',
                      },
                    })}
                  ></textarea>
                </div>
                {errors.instructions_bot && (
                  <Alert>* {errors.instructions_bot.message}</Alert>
                )}
              </div>
            </div>
            <div className="row justify-content-end bd col-12 mt-4">
              <a
                href="javascript:;"
                className="btn btn-lg btn-default mr-1"
                onClick={() => {
                  setModalBotAssistant(false);
                  reset();
                }}
              >
                Cancelar
              </a>
              <input
                className="btn btn-lg btn-info"
                type="submit"
                value="Crear"
              />
            </div>
          </form>
        </Content>
      </NodeModal>

      <Toaster containerClassName="su-toasts" />

      <Tooltip
        id="my-tooltip"
        style={{
          zIndex: 9999,
          backgroundColor: 'black',
          color: 'white',
          padding: '5px 10px',
          borderRadius: '5px',
          whiteSpace: 'pre-line',
          maxWidth: '500px',
        }}
      />
    </>
  );
};

const FlowWithProvider = () => {
  return (
    <ReactFlowProvider>
      <Flow />
    </ReactFlowProvider>
  );
};

export default FlowWithProvider;

const Alert = styledComponent.span`
  color: red;
  display: block;
  width: 100%;
  // width: 200px;
`;

const Content = styledComponent.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  h1 {
    font-size: 42px;
    font-weight: 700;
    margin-bottom: 10px;
    text-align: center;
  }
`;

